'use client'

import { useQuery, useQueryClient } from '@tanstack/react-query'
import { useAtomValue } from 'jotai'
import { selectAtom } from 'jotai/utils'
import { useLocale } from 'next-intl'
import { useTheme } from 'next-themes'
import dynamic from 'next/dynamic'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { ChartSkeleton } from '@/features/chart/components/widgets/chart-skeleton'
import ChartToolbar from '@/features/chart/components/widgets/chart-toolbar'
import OrderbookHeatmapOverlay from '@/features/chart/components/widgets/orderbook-heatmap-overlay'
import { useChartCompare } from '@/features/chart/hooks/use-chart-compare'
import { useChartOrderFormAppend } from '@/features/chart/hooks/use-chart-order-form-append'
import { useChartPriceScale } from '@/features/chart/hooks/use-chart-price-scale'
import { useChartRealtimeBridge } from '@/features/chart/hooks/use-chart-realtime-bridge'
import { useComparisonRealtimeBridge } from '@/features/chart/hooks/use-comparison-realtime-bridge'
import { useLevelProfile } from '@/features/chart/hooks/use-level-profile'
import { useOrderbookHeatmapViewport } from '@/features/chart/hooks/use-orderbook-heatmap'
import { useTradingOverlays } from '@/features/chart/hooks/use-trading-overlays'
import {
  PROFILE_SHOW_LABELS_INPUT_ID,
  sanitizeTVClientStyleState,
  useTVWidget,
} from '@/features/chart/hooks/use-tv-widget'
import { useVisibleRangeProfileViewport } from '@/features/chart/hooks/use-visible-range-profile'
import {
  shouldInitializeCompareScale,
  shouldProcessCompareCommand,
  shouldRemoveCompareStudy,
  shouldShowCompareSymbolLabels,
} from '@/features/chart/lib/chart-compare'
import { resolveChartPriceTick } from '@/features/chart/lib/chart-price-tick'
import {
  CHART_ANALYTICS_BY_KEY,
  CHART_ANALYTICS_DEFINITIONS,
  type ChartAnalyticsDefinition,
  getChartAnalyticsMetricByStudyName,
} from '@/features/chart/lib/config/chart-analytics'
import {
  computeInitialRequestedFamilies,
  planFamilyMaskUpdate,
} from '@/features/chart/lib/config/chart-families'
import { CHART_BUILTIN_STUDY_NAMES } from '@/features/chart/lib/config/chart-indicator-catalog'
import {
  CHART_COLORS,
  getCompareSeriesColor,
  PROFILE_LANE_COLORS,
} from '@/features/chart/lib/constants/chart-colors'
import {
  LIQUIDATION_LEVEL_PROFILE_EXCHANGES,
  selectLiquidationLevelBuckets,
  selectStopLevelBuckets,
} from '@/features/chart/lib/level-profile'
import {
  getLevelProfileKindByStudyName,
  LEVEL_PROFILE_MEASURE_INPUT_ID,
  LEVEL_PROFILE_STUDIES,
  type LevelProfileKind,
} from '@/features/chart/lib/level-profile-studies'
import {
  DEFAULT_ORDERBOOK_HEATMAP_GROUPING,
  DEFAULT_ORDERBOOK_HEATMAP_RANGE,
  formatOrderbookHeatmapSize,
  ORDERBOOK_HEATMAP_GROUPING_INPUT_ID,
  ORDERBOOK_HEATMAP_INDICATOR_ID,
  ORDERBOOK_HEATMAP_MAX_SIZE_INPUT_ID,
  ORDERBOOK_HEATMAP_MIN_SIZE_INPUT_ID,
  ORDERBOOK_HEATMAP_RANGE_INPUT_ID,
  ORDERBOOK_HEATMAP_STUDY,
  ORDERBOOK_HEATMAP_TIMEFRAMES,
  parseOrderbookHeatmapGrouping,
  parseOrderbookHeatmapRange,
  parseOrderbookHeatmapSize,
} from '@/features/chart/lib/orderbook-heatmap'
import {
  type ActiveIndicatorInstance,
  type IndicatorInstanceSettings,
  type IndicatorSettingsApi,
  type IndicatorSettingsInput,
  type IndicatorSettingsPlot,
  SINGLE_INSTANCE_INDICATOR_IDS,
} from '@/features/chart/lib/percept-indicator-utils'
import type { LaneDescriptor } from '@/features/chart/lib/profile-lane'
import {
  createFullBucketRangeProjector,
  createPriceRangeProjector,
} from '@/features/chart/lib/profile-lane-projection'
import {
  resolveMeasureLegendLabel,
  resolveVisibleRangeProfileLegendLabel,
  type VisibleRangeProfileLegendUnits,
} from '@/features/chart/lib/profile-legend-label'
import { selectSingleNamedStudy } from '@/features/chart/lib/profile-study-reconciliation'
import { createDefaultSeriesSourceRegistry } from '@/features/chart/lib/series-sources'
import {
  buildSeriesSubscriptionEntries,
  computeSeriesBackfillWindow,
} from '@/features/chart/lib/series-subscription-sync'
import {
  isProgrammaticStudyCreatePending,
  markProgrammaticStudyCreate,
} from '@/features/chart/lib/study-create-gate'
import {
  configureTradingPrimitive,
  dedupeTradesByAccountAndOrderId,
} from '@/features/chart/lib/trading-overlay-utils'
import { mountTVProfileCanvas } from '@/features/chart/lib/tv-profile-canvas'
import type { ChartRangePreset } from '@/features/chart/lib/utils/chart-util'
import {
  isOpenInterestProfileSupported,
  selectProfileBuckets,
} from '@/features/chart/lib/visible-range-profile'
import {
  DEFAULT_VISIBLE_RANGE_PROFILE_SETTINGS_BY_KIND,
  getVisibleRangeProfileKindByStudyName,
  VISIBLE_RANGE_PROFILE_ROW_SIZE_INPUT_ID,
  VISIBLE_RANGE_PROFILE_ROWS_LAYOUT_INPUT_ID,
  VISIBLE_RANGE_PROFILE_STUDIES,
  VISIBLE_RANGE_PROFILE_UNIT_INPUT_ID,
  VISIBLE_RANGE_PROFILE_VALUE_AREA_INPUT_ID,
  VISIBLE_RANGE_PROFILE_VOLUME_MODE_INPUT_ID,
  type VisibleRangeProfileDisplaySettings,
  type VisibleRangeProfileKind,
  type VisibleRangeProfileRowsLayout,
  type VisibleRangeProfileVolumeMode,
} from '@/features/chart/lib/visible-range-profile-studies'
import { compressAndZipChartConfig } from '@/features/chart/utils/chart-config-compression'
import { isChartStateUnchanged } from '@/features/chart/utils/chart-state-identity'
import { useWidgetConfigSync } from '@/features/dashboard/hooks/use-widget-config-sync'
import { watchlistEntryKey } from '@/features/dashboard/lib/watchlist-entry'
import { isPerpInstrument } from '@/features/hedge/lib/instrument-display'
import { resolveBaseCurrency } from '@/features/order/order-form/lib/instrument-resolution'
import { workingScaleLevels } from '@/features/scale/lib/scale-chart-levels'
import { useExchangeTradeability } from '@/hooks/use-can-trade-exchange'
import { useCapabilities } from '@/hooks/use-capabilities'
import { useCardAccountContext } from '@/hooks/use-card-account'
import {
  useLinkingGroupChartOption,
  useLinkingGroupWatchList,
} from '@/hooks/use-linking-group-state'
import { defaultLocale, isAppLocale } from '@/i18n/config'
import { track } from '@/lib/analytics'
import {
  CHART_TICK_RESOLUTION,
  CHART_TICK_TIMEFRAME,
  chartDataTimeframe,
} from '@/lib/chart-timeframe'
import { DEFAULT_VENUE_KEY } from '@/lib/configs/default-venue'
import { SUPPORTED_EXCHANGE } from '@/lib/configs/exchange-config'
import { exchangeToTradeVenue } from '@/lib/configs/exchange-registry'
import { toExchangeEnum } from '@/lib/proto/util'
import type { OrderXTradingOrderIntent } from '@/lib/tradingview/broker/orderx-broker-bridge'
import {
  parseQualifiedChartSymbol,
  qualifyChartSymbol,
} from '@/lib/tradingview/chart-symbol-identity'
import {
  ChartDatafeed,
  chartResolution,
  resolutionToSeconds,
} from '@/lib/tradingview/datafeeds/chart-datafeed'
import {
  type ChartInstrumentMetadata,
  ExchangeChartProvider,
} from '@/lib/tradingview/datafeeds/providers/exchange-chart-provider'
import { REFRESH_NONCE_INPUT_ID } from '@/lib/tradingview/datafeeds/series-sources'
import { nextRefreshNonce } from '@/lib/tradingview/study-inputs'
import type {
  TVChartApi,
  TVDrawingEventType,
  TVEntityId,
  TVPositionLine,
  TVStudyEventType,
} from '@/lib/tradingview/types/tv-widget.types'
import { formatSymbol } from '@/lib/utils/format-util'
import { fetchOrderHistory } from '@/service/account-history.api'
import { useUserAccounts } from '@/service/account.hook'
import type { fetchInstrumentList } from '@/service/instrument.api'
import { instrumentListQueryOptions, useInstrumentList } from '@/service/instrument.hook'
import { venueSchedulesQueryOptions } from '@/service/venue-status.hook'
import {
  allAlgoStatesAtom,
  allOpenOrdersAtom,
  allOrderHistoryAtom,
  allPositionsAtom,
} from '@/state/account'
import type { ChartCompareMode } from '@/state/chart-compare'
import { symbolFromTickerKey, watchListTickerMapAtom } from '@/state/dashboard'
import type {
  ChartAnalyticsMetric,
  LevelProfileId,
  LevelProfileMeasure,
  VisibleRangeProfileId,
  VisibleRangeProfileUnit,
} from '@/types/dashboard'
import { AccountStatus } from '@/types/generated/common/account'
import {
  AnalyticsType,
  Exchange,
  type Instrument,
  InstrumentKind,
  TradeVenue,
} from '@/types/generated/common/types'
import type { CandleFamily } from '@/types/generated/public_api'
import { UnifiedOrderItem } from '@/types/generated/public_api'

export interface ChartWidgetProps {
  cardId: string
  symbol?: string
  exchange?: keyof typeof SUPPORTED_EXCHANGE
  /** Bar width the data is fetched at — Tick passes 1s with `tick` set. */
  timeframe: number
  /** Tick (CHART_TICK_TIMEFRAME): the 1s bars drawn as one continuous line. */
  tick?: boolean
  /** Receives the picker value: a bar width, or CHART_TICK_TIMEFRAME for Tick. */
  onTimeframeChange: (timeframe: number) => void
  onRegisterDrawingToggle?: (toggle: () => boolean) => void
  onDrawingPanelVisibilityChange?: (isVisible: boolean) => void
  initialDrawingPanelVisible?: boolean
  showIndex?: boolean
  onAddChartTabRequest?: () => void
  onTypeToSearch?: (key: string) => void
  isFullscreen?: boolean
  onFullscreenToggle?: () => void
  realtimeEnabled?: boolean
}

const DEFAULT_TIMEFRAME = 60 * 60
/** TV's SeriesType for Area: Tick's continuous line with a soft fill. */
const TV_AREA_CHART_TYPE = 3
const TV_SIDE_SELL = -1
const EMPTY_LIVE_ROWS: never[] = []

const importIndicatorPickerDialog = () =>
  import('@/features/chart/components/dialog/percept/chart-indicator-picker-dialog')

const ChartIndicatorPickerDialog = dynamic(importIndicatorPickerDialog, { ssr: false })
const WatchlistWizard = dynamic(
  () =>
    import('@/features/dashboard/components/watchlist-wizard/watchlist-wizard').then(
      (module) => module.WatchlistWizard
    ),
  { ssr: false }
)

/** Warms the picker's lazy chunk ahead of the click that renders it. Without
 *  this the chunk fetch (and, in dev, its on-demand compile) starts only once
 *  the dialog is already supposed to be on screen — the source of the ~1s gap
 *  between pressing expand and the picker appearing. Module resolution is
 *  cached, so repeat calls are free. */
function preloadIndicatorPickerDialog() {
  void importIndicatorPickerDialog().catch(() => {})
}

// Settle time after the user stops panning before extending own-fetch series
// windows — onVisibleRangeChanged fires continuously mid-drag.
// Study 'create' events this soon after subscribing are the saved chart restoring,
// not the user adding an indicator.
const STUDY_RESTORE_SETTLE_MS = 2000
const VISIBLE_RANGE_SYNC_DEBOUNCE_MS = 400
// Window after a programmatic range change during which onVisibleRangeChanged is
// ignored, so TradingView's own follow-up events don't clear the active preset.
const VISIBLE_RANGE_GUARD_MS = 750
// Total setVisibleRange attempts per preset click (1 = initial, rest = re-asserts).
const VISIBLE_RANGE_APPLY_ATTEMPTS = 2
// Fallback delay when setVisibleRange doesn't return a promise to await.
const VISIBLE_RANGE_REAPPLY_DELAY_MS = 400
// Go to (a moment): this many bars framed around it.
const GO_TO_DATE_BARS = 120
// Venue calendars improve the time axis but must never block the chart itself.
const VENUE_SCHEDULE_RESOLVE_TIMEOUT_MS = 3_000
// livePrice is only a display/fallback value, but it lives in React state, so an
// unthrottled tick stream re-rendered this whole widget on every candle message.
// A same-origin iframe shares the parent's main thread, so that render churn
// showed up as sluggish hover inside the TV chart itself.
const LIVE_PRICE_COMMIT_MS = 250
// Coalesces a burst of own-fetch series landing their first data together
// (e.g. adding several ob-metrics studies at once) into one native reset
// instead of one per series.
const FIRST_DATA_RESET_DEBOUNCE_MS = 250
const PRICE_SCALE_MODE_MAP: Record<string, number> = {
  Normal: 0,
  Logarithmic: 1,
  Percentage: 2,
}
const PRICE_SCALE_MODE_NAMES = ['Normal', 'Logarithmic', 'Percentage'] as const
const INSTRUMENT_METADATA_WAIT_MS = 2000

const LIQUIDATION_PROFILE_EXCHANGES = new Set([
  Exchange.EXCHANGE_HYPERLIQUID,
  Exchange.EXCHANGE_BYBIT,
  Exchange.EXCHANGE_DERIBIT,
])
type VisibleRangeProfileEnabledKey =
  | 'volumeDeltaProfileEnabled'
  | 'liquidationProfileEnabled'
  | 'openInterestProfileEnabled'
type VisibleRangeProfileUnitKey =
  | 'volumeDeltaProfileUnit'
  | 'liquidationProfileUnit'
  | 'openInterestProfileUnit'
type VisibleRangeProfileShowLabelsKey =
  | 'volumeDeltaProfileShowLabels'
  | 'liquidationProfileShowLabels'
  | 'openInterestProfileShowLabels'
const VISIBLE_RANGE_PROFILE_OPTION_KEYS: Record<
  VisibleRangeProfileKind,
  {
    enabledKey: VisibleRangeProfileEnabledKey
    unitKey: VisibleRangeProfileUnitKey
    showLabelsKey: VisibleRangeProfileShowLabelsKey
  }
> = {
  volume: {
    enabledKey: 'volumeDeltaProfileEnabled',
    unitKey: 'volumeDeltaProfileUnit',
    showLabelsKey: 'volumeDeltaProfileShowLabels',
  },
  liquidation: {
    enabledKey: 'liquidationProfileEnabled',
    unitKey: 'liquidationProfileUnit',
    showLabelsKey: 'liquidationProfileShowLabels',
  },
  openInterest: {
    enabledKey: 'openInterestProfileEnabled',
    unitKey: 'openInterestProfileUnit',
    showLabelsKey: 'openInterestProfileShowLabels',
  },
}

// Hyperliquid liquidation & stop-level profiles (oems #261) — perps only, live-only, no
// range/pan-zoom re-fetch. Kept as a separate exchange gate/key map from
// LIQUIDATION_PROFILE_EXCHANGES/VISIBLE_RANGE_PROFILE_OPTION_KEYS above rather than merged into
// them: structurally different data pipeline (AnalyticsStream, not VisibleRangeProfileRequest).
type LevelProfileEnabledKey = 'liquidationLevelProfileEnabled' | 'stopLevelProfileEnabled'
type LevelProfileMeasureKey = 'liquidationLevelProfileMeasure' | 'stopLevelProfileMeasure'
type LevelProfileShowLabelsKey = 'liquidationLevelProfileShowLabels' | 'stopLevelProfileShowLabels'
const LEVEL_PROFILE_OPTION_KEYS: Record<
  LevelProfileKind,
  {
    enabledKey: LevelProfileEnabledKey
    measureKey: LevelProfileMeasureKey
    showLabelsKey: LevelProfileShowLabelsKey
  }
> = {
  liquidationLevels: {
    enabledKey: 'liquidationLevelProfileEnabled',
    measureKey: 'liquidationLevelProfileMeasure',
    showLabelsKey: 'liquidationLevelProfileShowLabels',
  },
  stopLevels: {
    enabledKey: 'stopLevelProfileEnabled',
    measureKey: 'stopLevelProfileMeasure',
    showLabelsKey: 'stopLevelProfileShowLabels',
  },
}

function parsePositivePrice(price?: string) {
  if (!price) return 0
  const parsed = Number(price)
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0
}

type TVPlusClickParams = {
  clientX?: number
  clientY?: number
  price?: number
  symbol?: string | null
}

type TVStudyInputValue = { id: string; value: string | number | boolean }

function readVisibleRangeProfileDisplaySettings(
  values: TVStudyInputValue[] | undefined
): VisibleRangeProfileDisplaySettings {
  const read = (id: string) => values?.find((input) => input.id === id)?.value
  const rowsLayoutValue = read(VISIBLE_RANGE_PROFILE_ROWS_LAYOUT_INPUT_ID)
  const volumeModeValue = read(VISIBLE_RANGE_PROFILE_VOLUME_MODE_INPUT_ID)
  const rowSizeValue = Number(read(VISIBLE_RANGE_PROFILE_ROW_SIZE_INPUT_ID))
  const valueAreaValue = Number(read(VISIBLE_RANGE_PROFILE_VALUE_AREA_INPUT_ID))

  const rowsLayout: VisibleRangeProfileRowsLayout =
    rowsLayoutValue === 'ticksPerRow' ? 'ticksPerRow' : 'numberOfRows'
  const volumeMode: VisibleRangeProfileVolumeMode =
    volumeModeValue === 'total' || volumeModeValue === 'delta' ? volumeModeValue : 'upDown'

  return {
    rowsLayout,
    rowSize: Number.isFinite(rowSizeValue) ? Math.max(1, Math.min(500, rowSizeValue)) : 24,
    volumeMode,
    valueAreaPercent: Number.isFinite(valueAreaValue)
      ? Math.max(0, Math.min(100, valueAreaValue))
      : 70,
  }
}

function areVisibleRangeProfileSettingsEqual(
  left: VisibleRangeProfileDisplaySettings,
  right: VisibleRangeProfileDisplaySettings
) {
  return (
    left.rowsLayout === right.rowsLayout &&
    left.rowSize === right.rowSize &&
    left.volumeMode === right.volumeMode &&
    left.valueAreaPercent === right.valueAreaPercent
  )
}

function resolveAnalyticsMetricByStudyName(studyName: string): ChartAnalyticsMetric | undefined {
  return studyName === 'OrderX Liquidation Split'
    ? 'buySellLiq'
    : studyName === 'OrderX Trade Count (Buy/Sell)'
      ? 'netBuyTradeCount'
      : getChartAnalyticsMetricByStudyName(studyName)
}

const ORDERX_MEASURE_LEGEND_LABELS = new Set<string>()
for (const definition of CHART_ANALYTICS_DEFINITIONS) {
  if (definition.inputs?.some((input) => input.id === 'measure')) {
    ORDERX_MEASURE_LEGEND_LABELS.add(definition.shortLabel)
  }
}
for (const definition of Object.values(VISIBLE_RANGE_PROFILE_STUDIES)) {
  ORDERX_MEASURE_LEGEND_LABELS.add(definition.shortDescription)
}

function containsUnqualifiedOrderXMeasureLabel(node: Node) {
  if (node.nodeType === 3) {
    return ORDERX_MEASURE_LEGEND_LABELS.has(node.textContent?.trim() ?? '')
  }

  const document = node.ownerDocument
  if (!document) return false
  const walker = document.createTreeWalker(node, NodeFilter.SHOW_TEXT)
  let textNode = walker.nextNode()
  while (textNode) {
    if (ORDERX_MEASURE_LEGEND_LABELS.has(textNode.textContent?.trim() ?? '')) return true
    textNode = walker.nextNode()
  }
  return false
}

function patchOrderXMeasureLegendLabels(
  iframe: HTMLIFrameElement,
  chart: TVChartApi,
  units: VisibleRangeProfileLegendUnits
) {
  const document = iframe.contentDocument
  if (!document?.body) return

  const analyticsUnitsByLabel = Object.fromEntries(
    CHART_ANALYTICS_DEFINITIONS.flatMap((definition) => {
      const measureInput = definition.inputs?.find((input) => input.id === 'measure')
      return measureInput
        ? [[definition.shortLabel, measureInput.defval === 'quote' ? 'quote' : 'base']]
        : []
    })
  ) as Record<string, VisibleRangeProfileUnit>
  chart.getAllStudies().forEach((study) => {
    const metric = resolveAnalyticsMetricByStudyName(study.name)
    const definition = metric ? CHART_ANALYTICS_BY_KEY[metric] : undefined
    if (!definition?.inputs?.some((input) => input.id === 'measure')) return

    try {
      const measure = chart
        .getStudyById(study.id)
        .getInputValues?.()
        .find((input) => input.id === 'measure')?.value
      analyticsUnitsByLabel[definition.shortLabel] = measure === 'quote' ? 'quote' : 'base'
    } catch {}
  })

  const walker = document.createTreeWalker(document.body, NodeFilter.SHOW_TEXT)
  let node = walker.nextNode()
  while (node) {
    const parent = node.parentElement
    const isInsideDialog = parent?.closest(
      '[role="dialog"], [data-dialog-name], [class*="dialog-"], [class*="popupDialog-"]'
    )
    if (!isInsideDialog) {
      const currentLabel = node.textContent?.trim() ?? ''
      const nextLabel =
        resolveMeasureLegendLabel(currentLabel, analyticsUnitsByLabel) ??
        resolveVisibleRangeProfileLegendLabel(currentLabel, units)
      if (nextLabel && nextLabel !== currentLabel) node.textContent = nextLabel
    }
    node = walker.nextNode()
  }
}

// The merged total "Volume" study (volumeBaseBlockIncl) is disabled in favor
// of native TV Volume — see replaceOrderXVolumeWithNative. Buy/Sell Volume
// stay custom since TV has no native equivalent with base/quote + block-trade
// settings.
const MAIN_PANE_VOLUME_METRICS = ['buyVolumeBaseBlockIncl', 'sellVolumeBaseBlockIncl'] as const

/** refreshNonce is a transient recompute trigger, never meaningful persisted
 *  state. Strip it from a layout snapshot so a nonce bump never registers as a
 *  layout change — the autosave content diff then reflects only genuine user
 *  edits (no nonce churn reaches the config service). */
function stripRefreshNonce(value: unknown): unknown {
  if (Array.isArray(value)) return value.map(stripRefreshNonce)
  if (!value || typeof value !== 'object') return value
  const next: Record<string, unknown> = {}
  for (const [key, item] of Object.entries(value as Record<string, unknown>)) {
    if (key === REFRESH_NONCE_INPUT_ID) continue
    next[key] = stripRefreshNonce(item)
  }
  return next
}

function getAnalyticsStudyMapFromWidget(
  widget: ReturnType<typeof useTVWidget>['widgetRef']['current']
): Map<string, string[]> {
  const studies = widget?.activeChart().getAllStudies() ?? []
  const map = new Map<string, string[]>()

  studies.forEach((study) => {
    // OrderX custom analytics
    const metric = resolveAnalyticsMetricByStudyName(study.name)
    if (metric) {
      const existing = map.get(metric) ?? []
      map.set(metric, [...existing, study.id])
      return
    }

    // TV built-in indicators
    const builtinEntry = Object.entries(CHART_BUILTIN_STUDY_NAMES).find(
      ([, { tvName }]) => tvName === study.name
    )
    if (builtinEntry) {
      const existing = map.get(builtinEntry[0]) ?? []
      map.set(builtinEntry[0], [...existing, study.id])
    }
  })

  return map
}

const VISIBLE_RANGE_PROFILE_INDICATOR_IDS: Record<VisibleRangeProfileKind, VisibleRangeProfileId> =
  {
    volume: 'volumeDeltaProfile',
    liquidation: 'liquidationProfile',
    openInterest: 'openInterestProfile',
  }
const LEVEL_PROFILE_INDICATOR_IDS: Record<LevelProfileKind, LevelProfileId> = {
  liquidationLevels: 'liquidationLevelProfile',
  stopLevels: 'stopLevelProfile',
}
const MAX_INSTANCE_PARAMS = 3

function formatStudyParams(chart: TVChartApi, studyId: TVEntityId): string {
  try {
    const values = chart.getStudyById(studyId).getInputValues?.() ?? []
    return values
      .filter(
        (input) =>
          input.id !== REFRESH_NONCE_INPUT_ID &&
          input.id !== 'symbol' &&
          (typeof input.value === 'number' || typeof input.value === 'string') &&
          input.value !== ''
      )
      .map((input) => String(input.value))
      .filter((value, index, all) => all.indexOf(value) === index)
      .slice(0, MAX_INSTANCE_PARAMS)
      .join(' · ')
  } catch {
    return ''
  }
}

/** Profiles style their plots transparent and paint bars themselves — no usable swatch. */
function isOpaqueColor(color: unknown): color is string {
  if (typeof color !== 'string' || !color || color === 'transparent') return false
  const alpha = color.match(/^rgba\([^)]*,\s*([\d.]+)\s*\)$/i)?.[1]
  return alpha === undefined || Number(alpha) > 0
}

function getStudyPlotColor(chart: TVChartApi, studyId: TVEntityId): string | null {
  try {
    const values = chart.getStudyById(studyId).getStyleValues?.()
    const plotColor = Object.values(values?.styles ?? {}).find(
      (style) => style?.visible !== false && style?.display !== 0 && isOpaqueColor(style?.color)
    )?.color
    if (plotColor) return plotColor
    for (const palette of Object.values(values?.palettes ?? {})) {
      const paletteColor = Object.values(palette?.colors ?? {}).find((entry) =>
        isOpaqueColor(entry.color)
      )?.color
      if (paletteColor) return paletteColor
    }
  } catch {}
  return null
}

/** Every picker-known study on the chart, one entry per study, in chart order. */
function getActiveIndicatorInstancesFromWidget(
  widget: ReturnType<typeof useTVWidget>['widgetRef']['current']
): ActiveIndicatorInstance[] {
  if (!widget) return []
  const chart = widget.activeChart()
  const instances: ActiveIndicatorInstance[] = []

  for (const study of chart.getAllStudies()) {
    const visibleRangeKind = getVisibleRangeProfileKindByStudyName(study.name)
    const levelKind = getLevelProfileKindByStudyName(study.name)
    const isHeatmap = study.name === ORDERBOOK_HEATMAP_STUDY.name
    const indicatorId = visibleRangeKind
      ? VISIBLE_RANGE_PROFILE_INDICATOR_IDS[visibleRangeKind]
      : levelKind
        ? LEVEL_PROFILE_INDICATOR_IDS[levelKind]
        : isHeatmap
          ? ORDERBOOK_HEATMAP_INDICATOR_ID
          : (resolveAnalyticsMetricByStudyName(study.name) ??
            Object.entries(CHART_BUILTIN_STUDY_NAMES).find(
              ([, { tvName }]) => tvName === study.name
            )?.[0])
    if (!indicatorId) continue

    let visible = true
    try {
      visible = chart.getStudyById(study.id).isVisible()
    } catch {}
    instances.push({
      studyId: study.id,
      indicatorId,
      // Profiles carry display settings, not identifying parameters.
      params: visibleRangeKind || levelKind || isHeatmap ? '' : formatStudyParams(chart, study.id),
      visible,
      color: getStudyPlotColor(chart, study.id),
    })
  }

  return instances
}

const SETTINGS_INPUT_TYPES = new Set(['bool', 'integer', 'float', 'text', 'source'])
/** TV's `display` bitmask: 15 draws the plot everywhere, 0 hides it. */
const PLOT_DISPLAY_ALL = 15
const PLOT_DISPLAY_NONE = 0

/** The study's editable inputs and plots, read fresh from TV each time. */
function readIndicatorInstanceSettings(
  chart: TVChartApi,
  studyId: TVEntityId
): IndicatorInstanceSettings | null {
  try {
    const study = chart.getStudyById(studyId)
    const values = new Map((study.getInputValues?.() ?? []).map((input) => [input.id, input.value]))
    const inputs: IndicatorSettingsInput[] = (study.getInputsInfo?.() ?? []).flatMap((info) => {
      const value = values.get(info.id)
      if (
        info.isHidden ||
        info.id === REFRESH_NONCE_INPUT_ID ||
        !SETTINGS_INPUT_TYPES.has(info.type) ||
        value === undefined
      ) {
        return []
      }
      return [
        {
          id: info.id,
          label: info.localizedName || info.name,
          type: info.type === 'source' ? 'text' : (info.type as IndicatorSettingsInput['type']),
          value,
          options: info.options?.map((option) => ({
            value: option,
            label: info.optionsTitles?.[option] ?? option,
          })),
          min: info.min,
          max: info.max,
        },
      ]
    })

    const titles = study.getStyleInfo?.().styles ?? {}
    const styles = study.getStyleValues?.().styles ?? {}
    const plots: IndicatorSettingsPlot[] = Object.entries(styles).flatMap(([plotId, style]) => {
      const title = titles[plotId]?.title
      // Profiles draw with transparent plots; there is no line to recolor.
      if (!title || !isOpaqueColor(style?.color)) return []
      return [
        {
          key: title,
          label: title,
          color: style.color,
          visible: style.display !== PLOT_DISPLAY_NONE,
        },
      ]
    })

    return { inputs, plots }
  } catch {
    return null
  }
}

function getCompareStudySymbol(chart: TVChartApi, studyId: TVEntityId): string | null {
  try {
    const values = chart.getStudyById(studyId).getInputValues?.() ?? []
    const symbolInput = values.find((input) => input.id === 'symbol')
    return typeof symbolInput?.value === 'string' ? symbolInput.value : null
  } catch {
    return null
  }
}

function applyCompareStudyPalette(chart: TVChartApi) {
  chart
    .getAllStudies()
    .filter((study) => study.name === 'Compare')
    .forEach((study, index) => {
      try {
        chart.getStudyById(study.id).applyOverrides({
          'plot.color': getCompareSeriesColor(index),
          'plot.linewidth': 2,
          'plot.transparency': 0,
        })
      } catch {}
    })
}

function syncCompareSymbolLabels(widget: ReturnType<typeof useTVWidget>['widgetRef']['current']) {
  if (!widget) return
  const studyNames = widget
    .activeChart()
    .getAllStudies()
    .map((study) => study.name)
  widget.applyOverrides({
    'scalesProperties.showSymbolLabels': shouldShowCompareSymbolLabels(studyNames),
  })
}

/** Migrates the merged OrderX total "Volume" study back to native TV Volume —
 *  the custom histogram scaling/z-order didn't hold up over the main series,
 *  native volume handles both natively. No-op once none remain. Buy/Sell
 *  Volume are untouched (they stay OrderX-only). */
function replaceOrderXVolumeWithNative(
  widget: ReturnType<typeof useTVWidget>['widgetRef']['current'],
  studyMap: Map<string, string[]>
): boolean {
  const orderXVolumeIds = studyMap.get('volumeBaseBlockIncl') ?? []
  if (orderXVolumeIds.length === 0) return false

  const chart = widget?.activeChart()
  if (!chart) return false

  for (const studyId of orderXVolumeIds) {
    try {
      chart.removeEntity(studyId)
    } catch {}
  }

  const hasNativeVolume = (studyMap.get('tv-volume') ?? []).length > 0
  if (!hasNativeVolume) {
    markProgrammaticStudyCreate(
      chart.createStudy('Volume', true, false, undefined, undefined, { priceScale: 'no-scale' })
    ).catch(() => {})
  }

  return true
}

function moveStudiesToMainPane(
  widget: ReturnType<typeof useTVWidget>['widgetRef']['current'],
  studyIds: readonly string[],
  priceScale: 'new-left' | 'new-right' | 'no-scale' | 'as-series'
) {
  const chart = widget?.activeChart()
  if (!chart) return

  for (const studyId of studyIds) {
    try {
      const study = chart.getStudyById(studyId)
      if (study.paneIndex() === 0) continue
      for (let attempt = 0; attempt < 8 && study.paneIndex() > 0; attempt += 1) {
        study.mergeUp()
      }
      study.changePriceScale(priceScale)
    } catch {}
  }
}

function applyOrderXOverlayColors(
  widget: ReturnType<typeof useTVWidget>['widgetRef']['current'],
  studyMap: Map<string, string[]>
) {
  const chart = widget?.activeChart()
  if (!chart) return

  for (const definition of CHART_ANALYTICS_DEFINITIONS) {
    if (definition.placement !== 'overlay') continue

    for (const studyId of studyMap.get(definition.key) ?? []) {
      try {
        chart.getStudyById(studyId).applyOverrides({
          [`${definition.shortLabel}.color`]: definition.color,
        })
      } catch {}
    }
  }
}

export default function TVChartWidget({
  cardId,
  symbol = 'BTCUSD',
  exchange = DEFAULT_VENUE_KEY,
  timeframe = DEFAULT_TIMEFRAME,
  tick = false,
  onTimeframeChange,
  onRegisterDrawingToggle,
  onDrawingPanelVisibilityChange,
  initialDrawingPanelVisible = true,
  showIndex = true,
  onAddChartTabRequest,
  onTypeToSearch,
  isFullscreen = false,
  onFullscreenToggle,
  realtimeEnabled = true,
}: ChartWidgetProps) {
  // 1. Custom hooks (no state injection)
  const { resolvedTheme } = useTheme()
  const rawLocale = useLocale()
  const chartLocale = isAppLocale(rawLocale) ? rawLocale : defaultLocale
  const queryClient = useQueryClient()
  const watchList = useLinkingGroupWatchList(cardId)
  const { chartOption, setChartOption } = useLinkingGroupChartOption(cardId)
  const { isAnalyticsTypeAllowed } = useCapabilities()
  const {
    acknowledge: acknowledgeCompareCommand,
    addAsCompare,
    command: compareCommand,
    allowedExchanges: compareAllowedExchanges,
    isResolved: isCompareUniverseResolved,
    linkingColor: compareLinkingColor,
  } = useChartCompare(cardId, { registerTarget: true })
  const { syncConfig } = useWidgetConfigSync()
  const timeframeRef = useRef(timeframe)
  timeframeRef.current = timeframe
  // What the picker shows (Tick or a bar width) — what TV's own interval changes compare to.
  const pickerTimeframe = tick ? CHART_TICK_TIMEFRAME : timeframe
  const pickerTimeframeRef = useRef(pickerTimeframe)
  pickerTimeframeRef.current = pickerTimeframe
  const chartTypeRef = useRef(chartOption?.chartType ?? 1)
  chartTypeRef.current = chartOption?.chartType ?? 1
  const chartContentRef = useRef(chartOption?.content)
  chartContentRef.current = chartOption?.content
  // Set while the timeframe-change effect below is mid-flight through its own
  // chart.setResolution() call, cleared once that call's callback finishes. TradingView's
  // native onIntervalChanged fires for ANY resolution change, including ones we just made
  // ourselves — without this guard, that echo would re-enter onTimeframeChange a second
  // time. Relying on comparing the reported resolution string against timeframeRef.current
  // isn't enough on its own: TradingView can echo a resolution back in a shape
  // resolutionToSeconds() doesn't map.
  const isSelfInitiatedResolutionChangeRef = useRef(false)
  // Set when TradingView's own controls changed the resolution (header interval picker,
  // native bottom-toolbar time frames like "1y"). TV has already switched resolution AND
  // framed the range by then, so the timeframe-change effect must not setResolution /
  // resetData / chartReset again — that would snap a "1y" preset back to the default zoom.
  const isNativeResolutionChangeRef = useRef(false)
  // Set by a range-preset click (chart-toolbar.tsx) right before it may call
  // onTimeframeChange. Read (and cleared) by the timeframe-change effect below so a
  // resolution change AND its accompanying visible-range change land together.
  const pendingVisibleRangeSecondsRef = useRef<number | null>(null)
  const rangeChangeGuardUntilRef = useRef(0)
  // Generation id + timer for applyVisibleRangeSeconds' re-assert pass.
  const visibleRangeRequestIdRef = useRef(0)
  const visibleRangeRetryTimeoutRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined)
  const [activeRangeSelection, setActiveRangeSelection] = useState<{
    label: string
    timeframe: number
  } | null>(null)
  const activeRangeSelectionRef = useRef(activeRangeSelection)

  // 2. Global state
  // The TradingView iframe stays mounted behind Option mode so its drawings and
  // studies survive the toggle. Stop its broad account subscriptions from waking
  // this very large component on every account tick while the iframe is hidden.
  const allPositions = useAtomValue(
    useMemo(
      () => selectAtom(allPositionsAtom, (rows) => (realtimeEnabled ? rows : EMPTY_LIVE_ROWS)),
      [realtimeEnabled]
    )
  )
  const allOpenOrders = useAtomValue(
    useMemo(
      () => selectAtom(allOpenOrdersAtom, (rows) => (realtimeEnabled ? rows : EMPTY_LIVE_ROWS)),
      [realtimeEnabled]
    )
  )
  const allAlgoStates = useAtomValue(
    useMemo(
      () => selectAtom(allAlgoStatesAtom, (rows) => (realtimeEnabled ? rows : EMPTY_LIVE_ROWS)),
      [realtimeEnabled]
    )
  )
  const allOrderHistory = useAtomValue(
    useMemo(
      () => selectAtom(allOrderHistoryAtom, (rows) => (realtimeEnabled ? rows : EMPTY_LIVE_ROWS)),
      [realtimeEnabled]
    )
  )
  const { tradeVenue: cardTradeVenue, accountId: chartAccountId } = useCardAccountContext(cardId)
  const { data: userAccountsData } = useUserAccounts({ showDisabled: true })
  const userAccounts = useMemo(() => userAccountsData?.accounts ?? [], [userAccountsData])
  // Instrument list for tick size / symbol info must match this tab's own exchange,
  // not the globally selected account exchange — otherwise tick size lookups for a
  // tab on a different exchange than the active account silently miss.
  const exchangeEnum = toExchangeEnum(exchange)
  const resolvedTradeVenue = exchangeToTradeVenue(exchangeEnum) ?? cardTradeVenue
  // No account that could take the order (a market-data-only market like XNAS/GLBX, or a venue
  // the user hasn't connected): the broker Buy/Sell buttons go disabled.
  const { tradeable } = useExchangeTradeability(exchangeEnum)
  const chartAccountIds = useMemo(() => (chartAccountId ? [chartAccountId] : []), [chartAccountId])
  const accountLabelById = useMemo(
    () => new Map(userAccounts.map((account) => [account.accountId, account.name])),
    [userAccounts]
  )
  const { data: chartOrderHistory } = useQuery({
    queryKey: ['chart-order-history', chartAccountId, symbol],
    queryFn: () =>
      fetchOrderHistory({
        accountId: chartAccountId!,
        accountIds: [],
        symbols: [symbol],
        currency: [],
        limit: 200,
      }),
    enabled: realtimeEnabled && Boolean(chartAccountId && symbol),
    staleTime: 30_000,
  })
  const positions = useMemo(
    () => allPositions.filter((position) => position.accountId === chartAccountId),
    [allPositions, chartAccountId]
  )
  const trades = useMemo(() => {
    const liveHistory = allOrderHistory.filter((item) => {
      const order = item.order
      return Boolean(order && order.accountId === chartAccountId && order.symbol === symbol)
    })
    const restHistory = (chartOrderHistory?.items ?? []).filter((item) => {
      const order = item.order
      return Boolean(order && order.accountId === chartAccountId && order.symbol === symbol)
    })

    // Keep the chart's deeper, symbol-scoped REST seed while layering the live coordinator
    // state on top. Live rows come first so an updated fill/status wins deduplication.
    return dedupeTradesByAccountAndOrderId([...liveHistory, ...restHistory])
  }, [allOrderHistory, chartAccountId, chartOrderHistory?.items, symbol])
  const openOrders = useMemo(
    () => allOpenOrders.filter((item) => item.order?.accountId === chartAccountId),
    [allOpenOrders, chartAccountId]
  )
  const scaleLevels = useMemo(
    () => workingScaleLevels(allAlgoStates, chartAccountId, symbol),
    [allAlgoStates, chartAccountId, symbol]
  )
  const { data: instrumentListData } = useInstrumentList(exchangeEnum)

  // 3. Local state
  const lastPushedBarTimeRef = useRef<number>(0)
  const timeframeChangeRequestIdRef = useRef(0)
  const symbolChangeRequestIdRef = useRef(0)
  // Keep trading primitives out of the chart while TradingView is replacing its
  // main series. Creating one during setSymbol() can leave its default "Position 0"
  // primitive orphaned inside the iframe before an adapter is available to remove.
  const symbolChangeInFlightRef = useRef(true)
  const [initialRequestedFamilies] = useState(() =>
    computeInitialRequestedFamilies(chartOption?.content)
  )
  const requestedFamiliesRef = useRef<CandleFamily[]>(initialRequestedFamilies)
  const refreshNonceRef = useRef(0)
  const firstDataResetTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const lastHandledCompareRequestIdRef = useRef(compareCommand?.requestId ?? null)
  const previousCompareLinkingColorRef = useRef(compareLinkingColor)
  const compareScaleInitializedRef = useRef(false)
  const tradesRef = useRef<UnifiedOrderItem[]>(trades)
  const positionsRef = useRef(positions)
  const openOrdersRef = useRef(openOrders)
  const scaleLevelsRef = useRef(scaleLevels)
  // Constructed synchronously on first render (not in an effect) so it's already
  // in place by the time the widget-creation effect's ensureTVScript() promise
  // resolves, instead of racing a separate post-mount effect for it.
  const [datafeed] = useState(() => {
    const resolveInstrumentMetadata = async (identity: {
      exchange: Exchange
      symbol: string
    }): Promise<ChartInstrumentMetadata | undefined> => {
      const options = instrumentListQueryOptions(identity.exchange)
      let data = queryClient.getQueryData<Awaited<ReturnType<typeof fetchInstrumentList>>>(
        options.queryKey
      )
      if (!data && identity.exchange === Exchange.EXCHANGE_XKRX) {
        // KRX symbols are native numeric codes. Wait for their human-readable
        // metadata so the first chart resolve does not permanently keep the
        // code as its legend label.
        try {
          data = await queryClient.fetchQuery(options)
        } catch {
          return undefined
        }
      } else if (!data) {
        // TV resolves a symbol once and never again, so resolving without the catalog fixed
        // the price format at the 2-decimal fallback for the chart's lifetime. Give a cold
        // catalog a short window; past it, first paint wins.
        let timeoutId: ReturnType<typeof setTimeout> | undefined
        try {
          data = await Promise.race([
            queryClient.fetchQuery(options),
            new Promise<never>((_, reject) => {
              timeoutId = setTimeout(
                () => reject(new Error('instrument catalog timeout')),
                INSTRUMENT_METADATA_WAIT_MS
              )
            }),
          ])
        } catch {
          return undefined
        } finally {
          clearTimeout(timeoutId)
        }
      }
      if (!data) return undefined
      const instrument = data.instruments.find((candidate) => candidate.symbol === identity.symbol)
      if (!instrument) return undefined
      // The tick for the current price band, not the finest band the venue publishes.
      const tick = await resolveChartPriceTick(instrument, identity.exchange)
      return {
        tickSize: tick ? String(tick) : instrument.tickSize,
        displayName: instrument.enrichment?.displayName,
        description: instrument.enrichment?.description,
      }
    }
    const resolveVenueSchedule = async (exchange: Exchange) => {
      let list = queryClient.getQueryData(venueSchedulesQueryOptions.queryKey)
      if (!list) {
        let timeoutId: ReturnType<typeof setTimeout> | undefined
        try {
          list = await Promise.race([
            queryClient.fetchQuery(venueSchedulesQueryOptions),
            new Promise<never>((_, reject) => {
              timeoutId = setTimeout(
                () => reject(new Error('venue schedules timed out')),
                VENUE_SCHEDULE_RESOLVE_TIMEOUT_MS
              )
            }),
          ])
        } catch (error) {
          console.warn('venue schedules unavailable; chart renders without holidays', error)
          return undefined
        } finally {
          if (timeoutId !== undefined) clearTimeout(timeoutId)
        }
      }
      return list.find((schedule) => schedule.exchange === exchange)
    }
    return new ChartDatafeed(
      new ExchangeChartProvider(resolveInstrumentMetadata, resolveVenueSchedule),
      createDefaultSeriesSourceRegistry()
    )
  })
  const datafeedRef = useRef<ChartDatafeed | null>(datafeed)
  datafeed.setPrimarySeries({ exchange: exchangeEnum, symbol })
  datafeed.setRequestedFamilies(requestedFamiliesRef.current)

  // Run the first candle request in parallel with TradingView script/widget
  // initialization. getBars adopts this in-flight response when its identity,
  // timeframe, and family mask match.
  useEffect(() => {
    void datafeed.primeInitialBars({ exchange: exchangeEnum, symbol }, timeframe).catch(() => {})
  }, [datafeed, exchangeEnum, symbol, timeframe])
  const visibleRangeProfileStudyIdsRef = useRef<
    Partial<Record<VisibleRangeProfileKind, TVEntityId>>
  >({})
  const visibleRangeProfileStudyCreationRef = useRef<
    Partial<Record<VisibleRangeProfileKind, Promise<TVEntityId | null>>>
  >({})
  const visibleRangeProfileEnabledRef = useRef<Record<VisibleRangeProfileKind, boolean>>({
    volume: false,
    liquidation: false,
    openInterest: false,
  })
  const levelProfileStudyIdsRef = useRef<Partial<Record<LevelProfileKind, TVEntityId>>>({})
  const levelProfileStudyCreationRef = useRef<
    Partial<Record<LevelProfileKind, Promise<TVEntityId | null>>>
  >({})
  const levelProfileEnabledRef = useRef<Record<LevelProfileKind, boolean>>({
    liquidationLevels: false,
    stopLevels: false,
  })
  const heatmapStudyIdRef = useRef<TVEntityId | null>(null)
  const heatmapStudyCreationRef = useRef<Promise<TVEntityId | null> | null>(null)
  const heatmapEnabledRef = useRef(false)
  const indexPriceLineRef = useRef<TVPositionLine | null>(null)
  const indexPriceLineRequestIdRef = useRef(0)
  const recreateIndexPriceLineRef = useRef<() => void>(() => {})
  const lastPlusClickRef = useRef<(TVPlusClickParams & { receivedAt: number }) | null>(null)
  const openLimitOrderFormAtPriceRef = useRef<
    ((price: number, side: 'buy' | 'sell', params?: TVPlusClickParams) => void) | null
  >(null)
  const [livePrice, setLivePrice] = useState(0)
  const livePriceRef = useRef(0)
  const livePriceCommitTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const [isExpandedIndicatorPickerOpen, setIsExpandedIndicatorPickerOpen] = useState(false)
  const [isComparePickerOpen, setIsComparePickerOpen] = useState(false)
  const [visibleRangeProfileSettingsByKind, setVisibleRangeProfileSettingsByKind] = useState(() =>
    structuredClone(DEFAULT_VISIBLE_RANGE_PROFILE_SETTINGS_BY_KIND)
  )

  // 4. Derived values
  const colorTheme = resolvedTheme === 'dark' ? 'dark' : 'light'
  const colorThemeRef = useRef<'light' | 'dark'>(colorTheme)
  colorThemeRef.current = colorTheme

  // Like symbolTickSize below: derived from instrumentListData (this tab's own
  // exchange), not useInstrumentBySymbol/globalInstrumentsAtom (the globally
  // selected account's exchange). A tab open on a different exchange than the
  // active account (e.g. an Upbit tab while a non-Upbit account is selected)
  // otherwise gets `null` here, and callers below silently fall back to
  // Deribit-format string parsing for base/quote currency (see
  // parseInstrumentSymbol) — e.g. 'KRW-BTC' parsed as base=KRW, quote=USD.
  const symbolInstrument = useMemo(
    () => instrumentListData?.instruments.find((inst) => inst.symbol === symbol) ?? null,
    [instrumentListData, symbol]
  )

  const [activeAnalytics, setActiveAnalytics] = useState<ChartAnalyticsMetric[]>([])
  const [indicatorInstances, setIndicatorInstances] = useState<ActiveIndicatorInstance[]>([])

  const symbolTickSize = symbolInstrument?.tickSize || undefined
  const isOption = symbolInstrument?.kind === InstrumentKind.INSTRUMENT_KIND_OPTION
  const volumeDeltaProfileEnabled = chartOption?.volumeDeltaProfileEnabled ?? false
  const liquidationProfileEnabled =
    (chartOption?.liquidationProfileEnabled ?? false) &&
    LIQUIDATION_PROFILE_EXCHANGES.has(exchangeEnum)
  const openInterestProfileEnabled =
    (chartOption?.openInterestProfileEnabled ?? false) &&
    isOpenInterestProfileSupported(symbolInstrument)
  const visibleRangeProfileEnabled =
    !isOption &&
    (volumeDeltaProfileEnabled || liquidationProfileEnabled || openInterestProfileEnabled)
  const legacyVisibleRangeProfileUnit = chartOption?.visibleRangeProfileUnit ?? 'quote'
  const volumeDeltaProfileUnit =
    chartOption?.volumeDeltaProfileUnit ?? legacyVisibleRangeProfileUnit
  const liquidationProfileUnit =
    chartOption?.liquidationProfileUnit ?? legacyVisibleRangeProfileUnit
  const openInterestProfileUnit = chartOption?.openInterestProfileUnit ?? 'quote'
  const volumeDeltaProfileShowLabels = chartOption?.volumeDeltaProfileShowLabels ?? true
  const liquidationProfileShowLabels = chartOption?.liquidationProfileShowLabels ?? true
  const openInterestProfileShowLabels = chartOption?.openInterestProfileShowLabels ?? true
  const visibleRangeProfileRequestSettings = useMemo(
    () => [
      ...(volumeDeltaProfileEnabled ? [visibleRangeProfileSettingsByKind.volume] : []),
      ...(liquidationProfileEnabled ? [visibleRangeProfileSettingsByKind.liquidation] : []),
      ...(openInterestProfileEnabled ? [visibleRangeProfileSettingsByKind.openInterest] : []),
    ],
    [
      liquidationProfileEnabled,
      openInterestProfileEnabled,
      visibleRangeProfileSettingsByKind,
      volumeDeltaProfileEnabled,
    ]
  )
  const visibleRangeProfileLegendUnits = useMemo<VisibleRangeProfileLegendUnits>(
    () => ({
      volume: volumeDeltaProfileUnit,
      liquidation: liquidationProfileUnit,
      openInterest: openInterestProfileUnit,
    }),
    [liquidationProfileUnit, openInterestProfileUnit, volumeDeltaProfileUnit]
  )
  visibleRangeProfileEnabledRef.current = {
    volume: volumeDeltaProfileEnabled,
    liquidation: liquidationProfileEnabled,
    openInterest: openInterestProfileEnabled,
  }

  const symbolPriceDecimals = useMemo(() => {
    if (!symbolTickSize) return 2
    return symbolTickSize.includes('.') ? symbolTickSize.split('.')[1].length : 0
  }, [symbolTickSize])

  // Narrowed tickerMap subscription: this widget only ever reads its own
  // symbol's lastPrice plus a resolved index-line price. Subscribing to the
  // whole map re-rendered this (very large) component on every tick of every
  // streamed symbol — the selectAtom slice only re-renders when one of these
  // two values actually changes.
  const indexBaseCurrency = useMemo(() => {
    const currentItem = watchList?.list[watchList?.selectedIndex]
    return resolveBaseCurrency(
      [],
      currentItem?.symbol ?? symbol,
      currentItem?.symbol === symbol ? symbolInstrument : null
    )
  }, [symbol, symbolInstrument, watchList?.list, watchList?.selectedIndex])

  const {
    lastPrice: symbolLastPrice,
    markPrice: symbolMarkPrice,
    bestBidPrice: symbolBestBidPrice,
    bestAskPrice: symbolBestAskPrice,
    indexLinePrice,
  } = useAtomValue(
    useMemo(
      () =>
        selectAtom(
          watchListTickerMapAtom,
          (map) => {
            if (!realtimeEnabled) {
              return {
                lastPrice: undefined,
                markPrice: undefined,
                bestBidPrice: undefined,
                bestAskPrice: undefined,
                indexLinePrice: 0,
              }
            }
            const own = map[watchlistEntryKey(exchangeEnum, symbol)]
            let indexPrice = parsePositivePrice(own?.indexPrice)
            if (indexPrice <= 0) {
              const perpKey = Object.keys(map).find((sym) => {
                const upper = symbolFromTickerKey(sym).toUpperCase()
                return upper.startsWith(indexBaseCurrency + '-') && upper.includes('PERP')
              })
              indexPrice =
                parsePositivePrice(perpKey ? map[perpKey]?.indexPrice : undefined) ||
                parsePositivePrice(perpKey ? map[perpKey]?.lastPrice : undefined)
            }
            return {
              lastPrice: own?.lastPrice,
              markPrice: own?.markPrice,
              bestBidPrice: own?.bestBidPrice,
              bestAskPrice: own?.bestAskPrice,
              indexLinePrice: indexPrice,
            }
          },
          (a, b) =>
            a.lastPrice === b.lastPrice &&
            a.markPrice === b.markPrice &&
            a.bestBidPrice === b.bestBidPrice &&
            a.bestAskPrice === b.bestAskPrice &&
            a.indexLinePrice === b.indexLinePrice
        ),
      [exchangeEnum, symbol, indexBaseCurrency, realtimeEnabled]
    )
  )
  const indexLinePriceRef = useRef(indexLinePrice)
  indexLinePriceRef.current = indexLinePrice
  const hasIndexLinePrice = indexLinePrice > 0

  const currentOrderPrice = useMemo(() => {
    const tickerPrice = Number(symbolLastPrice)
    if (Number.isFinite(tickerPrice) && tickerPrice > 0) return tickerPrice
    if (Number.isFinite(livePrice) && livePrice > 0) return livePrice
    return null
  }, [livePrice, symbolLastPrice])

  const chartLiveMarkPrice =
    symbolMarkPrice ?? symbolLastPrice ?? (livePrice > 0 ? livePrice.toString() : undefined)

  const formatContextMenuPrice = useCallback(
    (price: number) => {
      const factor = Math.pow(10, symbolPriceDecimals)
      const floored = Math.floor(price * factor) / factor
      return floored.toLocaleString('en-US', {
        minimumFractionDigits: symbolPriceDecimals,
        maximumFractionDigits: symbolPriceDecimals,
      })
    },
    [symbolPriceDecimals]
  )

  const contextMenuItemsProcessor = useCallback(
    (
      items: readonly unknown[],
      actionsFactory: {
        createAction: (options: {
          actionId: string
          label: string
          onExecute?: () => void
        }) => unknown
        createSeparator?: () => unknown
      },
      params: { menuName: string; detail?: { type?: string } }
    ) => {
      const lastPlusClick = lastPlusClickRef.current
      const isPriceScaleMenu =
        params.detail?.type === 'priceScale' || params.menuName.toLowerCase().includes('crosshair')
      if (!isPriceScaleMenu || !lastPlusClick || Date.now() - lastPlusClick.receivedAt > 1000) {
        return items
      }

      const menuPrice = lastPlusClick.price
      if (typeof menuPrice !== 'number' || !Number.isFinite(menuPrice) || menuPrice <= 0) {
        return items
      }

      const formattedPrice = formatContextMenuPrice(menuPrice)
      const menuSymbol = lastPlusClick.symbol ?? symbol
      const suffix = `${formatSymbol(menuSymbol)} at ${formattedPrice}`
      const orderActions = [
        actionsFactory.createAction({
          actionId: `orderx-buy-${symbol}-${menuPrice}`,
          label: `Buy ${suffix}`,
          onExecute: () => openLimitOrderFormAtPriceRef.current?.(menuPrice, 'buy', lastPlusClick),
        }),
        actionsFactory.createAction({
          actionId: `orderx-sell-${symbol}-${menuPrice}`,
          label: `Sell ${suffix}`,
          onExecute: () => openLimitOrderFormAtPriceRef.current?.(menuPrice, 'sell', lastPlusClick),
        }),
      ]

      const separator = actionsFactory.createSeparator?.()
      return separator ? [...orderActions, separator, ...items] : [...orderActions, ...items]
    },
    [formatContextMenuPrice, symbol]
  )

  const handleTradingOrderIntent = useCallback(
    ({ order }: OrderXTradingOrderIntent) => {
      const side = order.side === TV_SIDE_SELL ? 'sell' : 'buy'
      // These intents come from TradingView's own BUY/SELL buttons. Their prefilled
      // limit/stop price and quotes come from TradingView's internal quote state, which
      // our broker bridge never feeds — and the widget is reused across chart tabs, so
      // they can still hold the PREVIOUS symbol's price (an ETH price on a BTC chart).
      // This chart's own live side quote comes first: buy fills at the best ask, sell at
      // the best bid. (Explicit-price actions — the context menu, the price-scale "+" —
      // call openLimitOrderFormAtPrice directly, not through here.)
      const liveSideQuote = Number(side === 'buy' ? symbolBestAskPrice : symbolBestBidPrice)
      const tvQuote = side === 'buy' ? order.currentQuotes?.ask : order.currentQuotes?.bid
      const price = [
        liveSideQuote,
        currentOrderPrice,
        order.limitPrice,
        order.stopPrice,
        tvQuote,
      ].find(
        (candidate): candidate is number =>
          typeof candidate === 'number' && Number.isFinite(candidate) && candidate > 0
      )
      if (price === undefined) return

      openLimitOrderFormAtPriceRef.current?.(price, side)
    },
    [currentOrderPrice, symbolBestAskPrice, symbolBestBidPrice]
  )

  // The header button toggles, so pressing Indicators again closes the picker.
  const handleIndicatorPickerClick = useCallback(
    () => setIsExpandedIndicatorPickerOpen((open) => !open),
    []
  )
  const handleIndicatorPickerOpen = useCallback(() => setIsExpandedIndicatorPickerOpen(true), [])
  const handleCompareClick = useCallback(() => setIsComparePickerOpen(true), [])
  const handleCompareSelect = useCallback(
    (instrument: Instrument) => {
      addAsCompare([{ exchange: instrument.exchange, symbol: instrument.symbol }])
    },
    [addAsCompare]
  )
  const compareMenuOptions = useMemo(
    () => ({
      onCompare: (instrument: Instrument, mode: ChartCompareMode) =>
        addAsCompare([{ exchange: instrument.exchange, symbol: instrument.symbol }], mode),
      requireMode: true,
    }),
    [addAsCompare]
  )

  // Custom hooks with state injection (after 3 & 4)
  const { containerRef, widgetRef, isChartReady } = useTVWidget({
    symbol,
    timeframe,
    tick,
    datafeedRef,
    savedData: chartOption?.content,
    colorTheme,
    locale: chartLocale,
    contextMenuItemsProcessor,
    onTradingOrderIntent: handleTradingOrderIntent,
    tradable: tradeable,
    onIndicatorPickerClick: handleIndicatorPickerClick,
    onIndicatorPickerOpen: handleIndicatorPickerOpen,
    onIndicatorPickerPrefetch: preloadIndicatorPickerDialog,
    onCompareClick: handleCompareClick,
    onAddChartTabRequest,
    onTypeToSearch,
    isFullscreen,
    onFullscreenToggle,
    // onReady is stored in a ref inside useTVWidget and called async — loadOrderOverlays is safe to reference
    onReady: () => {
      if (!symbolChangeInFlightRef.current) loadOrderOverlays(symbol, timeframe)
    },
  })

  const clearIndexPriceLine = useCallback(() => {
    indexPriceLineRequestIdRef.current += 1
    const line = indexPriceLineRef.current
    indexPriceLineRef.current = null
    try {
      line?.remove()
    } catch {}
  }, [])

  const recreateIndexPriceLine = useCallback(() => {
    if (symbolChangeInFlightRef.current) return
    clearIndexPriceLine()
    const widget = widgetRef.current
    if (!isChartReady || !widget || !showIndex || !hasIndexLinePrice) return

    const requestId = indexPriceLineRequestIdRef.current
    widget
      .chart()
      .createPositionLine()
      .then((line) => {
        if (indexPriceLineRequestIdRef.current !== requestId) {
          try {
            line.remove()
          } catch {}
          return
        }
        const configuredLine = configureTradingPrimitive(line, (indexLine) => {
          indexLine
            .setPrice(indexLinePriceRef.current)
            .setLineColor(CHART_COLORS.profit)
            .setLineStyle(2)
            .setLineWidth(1)
            .setText('Index')
            .setBodyTextColor(CHART_COLORS.profit)
            .setBodyBorderColor(CHART_COLORS.profit)
            .setBodyBackgroundColor(CHART_COLORS.profitSubtle)
            .setQuantity('')
            .setQuantityBackgroundColor(CHART_COLORS.transparent)
            .setQuantityBorderColor(CHART_COLORS.transparent)
        })
        if (configuredLine) indexPriceLineRef.current = configuredLine
      })
      .catch(() => {})
  }, [clearIndexPriceLine, hasIndexLinePrice, isChartReady, showIndex, widgetRef])

  useEffect(() => {
    recreateIndexPriceLineRef.current = recreateIndexPriceLine
  }, [recreateIndexPriceLine])

  const patchMeasureLegendLabels = useCallback(() => {
    const iframe = containerRef.current?.querySelector('iframe')
    const chart = widgetRef.current?.activeChart()
    if (!iframe || !chart) return
    patchOrderXMeasureLegendLabels(iframe, chart, visibleRangeProfileLegendUnits)
  }, [containerRef, visibleRangeProfileLegendUnits, widgetRef])

  const visibleRangeProfileQuery = useVisibleRangeProfileViewport({
    widget: widgetRef.current,
    isChartReady,
    enabled: visibleRangeProfileEnabled && realtimeEnabled,
    exchange: exchangeEnum,
    symbol,
    includePriceRange: chartOption?.scale !== 'Percentage',
    settings: visibleRangeProfileRequestSettings,
    tickSize: symbolTickSize ? Number(symbolTickSize) : undefined,
  })

  // Rendering-only — never used to build a request (these profiles take no price-range request
  // field at all). Polled whenever the chart is ready, independent of whether a level-profile
  // indicator is currently toggled on, since it's also what decides whether one CAN be toggled on
  // (isNormalNonInvertedScale below).
  const chartPriceScale = useChartPriceScale({
    widget: widgetRef.current,
    isChartReady,
    enabled: isChartReady && realtimeEnabled,
    containerRef,
  })

  // Producer-owned grid, projected onto the chart's own visible price range for rendering — that
  // projection is only correct for a linear, non-inverted axis, so gate on both rather than
  // silently mis-positioning bars under log/percentage/inverted scales (v1 scope limit).
  const isNormalNonInvertedScale =
    (chartOption?.scale ?? 'Normal') === 'Normal' && !chartPriceScale.isInverted
  const isHyperliquidPerp =
    LIQUIDATION_LEVEL_PROFILE_EXCHANGES.has(exchangeEnum) &&
    isPerpInstrument({
      symbol: symbolInstrument?.symbol ?? '',
      fundingInterval: symbolInstrument?.fundingInterval,
    })
  // PRO analytics: a layout saved on a paid plan keeps the toggle, but a plan
  // without the type never opens the stream (the backend would refuse it).
  const liquidationLevelProfileEnabled =
    (chartOption?.liquidationLevelProfileEnabled ?? false) &&
    isHyperliquidPerp &&
    isNormalNonInvertedScale &&
    isAnalyticsTypeAllowed(AnalyticsType.ANALYTICS_TYPE_LIQUIDATION_LEVELS)
  const stopLevelProfileEnabled =
    (chartOption?.stopLevelProfileEnabled ?? false) &&
    isHyperliquidPerp &&
    isNormalNonInvertedScale &&
    isAnalyticsTypeAllowed(AnalyticsType.ANALYTICS_TYPE_STOP_LEVELS)
  const levelProfileEnabled = liquidationLevelProfileEnabled || stopLevelProfileEnabled
  const liquidationLevelProfileMeasure = chartOption?.liquidationLevelProfileMeasure ?? 'notional'
  const stopLevelProfileMeasure = chartOption?.stopLevelProfileMeasure ?? 'notional'
  const liquidationLevelProfileShowLabels = chartOption?.liquidationLevelProfileShowLabels ?? true
  const stopLevelProfileShowLabels = chartOption?.stopLevelProfileShowLabels ?? true
  levelProfileEnabledRef.current = {
    liquidationLevels: liquidationLevelProfileEnabled,
    stopLevels: stopLevelProfileEnabled,
  }

  const liquidationLevelProfile = useLevelProfile(
    AnalyticsType.ANALYTICS_TYPE_LIQUIDATION_LEVELS,
    exchangeEnum,
    symbol,
    liquidationLevelProfileEnabled && realtimeEnabled
  )
  const stopLevelProfile = useLevelProfile(
    AnalyticsType.ANALYTICS_TYPE_STOP_LEVELS,
    exchangeEnum,
    symbol,
    stopLevelProfileEnabled && realtimeEnabled
  )

  // Columns are placed by linear time and price, so the same scale gate as the level profiles,
  // plus fixed-width bars (no monthly).
  const isOrderbookHeatmapSupported =
    !isOption && isNormalNonInvertedScale && ORDERBOOK_HEATMAP_TIMEFRAMES.has(timeframe)
  const orderbookHeatmapEnabled =
    (chartOption?.orderbookHeatmapEnabled ?? false) && isOrderbookHeatmapSupported
  heatmapEnabledRef.current = orderbookHeatmapEnabled
  const orderbookHeatmapVisible =
    orderbookHeatmapEnabled &&
    indicatorInstances.some(
      (instance) => instance.indicatorId === ORDERBOOK_HEATMAP_INDICATOR_ID && instance.visible
    )
  const orderbookHeatmap = useOrderbookHeatmapViewport({
    widget: widgetRef.current,
    isChartReady,
    enabled: orderbookHeatmapVisible && realtimeEnabled,
    exchange: exchangeEnum,
    symbol,
    timeframe,
  })
  const orderbookHeatmapRange =
    parseOrderbookHeatmapRange(chartOption?.orderbookHeatmapRange) ??
    DEFAULT_ORDERBOOK_HEATMAP_RANGE
  const orderbookHeatmapRangeRef = useRef(orderbookHeatmapRange)
  orderbookHeatmapRangeRef.current = orderbookHeatmapRange
  const orderbookHeatmapGrouping =
    parseOrderbookHeatmapGrouping(chartOption?.orderbookHeatmapGrouping) ??
    DEFAULT_ORDERBOOK_HEATMAP_GROUPING
  const orderbookHeatmapGroupingRef = useRef(orderbookHeatmapGrouping)
  orderbookHeatmapGroupingRef.current = orderbookHeatmapGrouping
  const orderbookHeatmapMinSize = parseOrderbookHeatmapSize(chartOption?.orderbookHeatmapMinSize)
  const orderbookHeatmapMaxSize = parseOrderbookHeatmapSize(chartOption?.orderbookHeatmapMaxSize)
  const orderbookHeatmapSizeFilter = useMemo(
    () => ({ min: orderbookHeatmapMinSize, max: orderbookHeatmapMaxSize }),
    [orderbookHeatmapMaxSize, orderbookHeatmapMinSize]
  )
  const orderbookHeatmapSizeFilterRef = useRef(orderbookHeatmapSizeFilter)
  orderbookHeatmapSizeFilterRef.current = orderbookHeatmapSizeFilter

  const getMainPriceScale = useCallback(() => {
    const panes = widgetRef.current?.chart().getPanes()
    const mainPane = panes?.[0]
    return mainPane?.getMainSourcePriceScale?.() ?? mainPane?.getRightPriceScales()[0] ?? null
  }, [widgetRef])

  const syncActiveAnalyticsFromChart = useCallback(() => {
    const studyMap = getAnalyticsStudyMapFromWidget(widgetRef.current)
    if (replaceOrderXVolumeWithNative(widgetRef.current, studyMap)) return
    applyOrderXOverlayColors(widgetRef.current, studyMap)
    moveStudiesToMainPane(widgetRef.current, studyMap.get('parkinsonVolatility') ?? [], 'new-left')
    moveStudiesToMainPane(widgetRef.current, studyMap.get('tv-volume') ?? [], 'no-scale')
    moveStudiesToMainPane(
      widgetRef.current,
      MAIN_PANE_VOLUME_METRICS.flatMap((metric) => studyMap.get(metric) ?? []),
      'no-scale'
    )
    const nextAnalytics: ChartAnalyticsMetric[] = []
    studyMap.forEach((studyIds, metric) => {
      studyIds.forEach(() => nextAnalytics.push(metric as ChartAnalyticsMetric))
    })
    setActiveAnalytics(nextAnalytics)
    setIndicatorInstances(getActiveIndicatorInstancesFromWidget(widgetRef.current))

    // Family mask: narrow the shared candle fetch to CORE + what the active
    // studies need. A widen means already-loaded candles miss columns, so
    // force a native refetch + full study recompute (Q1 fallback mechanism).
    const { families, widened } = planFamilyMaskUpdate(requestedFamiliesRef.current, nextAnalytics)
    requestedFamiliesRef.current = families
    datafeedRef.current?.setRequestedFamilies(families)
    if (widened) {
      try {
        widgetRef.current?.resetCache()
        widgetRef.current?.activeChart().resetData()
      } catch {}
    }
  }, [widgetRef])

  const syncSeriesSubscriptions = useCallback(() => {
    const widget = widgetRef.current
    const datafeed = datafeedRef.current
    if (!widget || !datafeed) return
    const chart = widget.activeChart()

    let visibleRange: { from: number; to: number } | null = null
    try {
      visibleRange = chart.getVisibleRange()
    } catch {}

    const nowSec = Math.floor(Date.now() / 1000)
    const entries = buildSeriesSubscriptionEntries(
      getAnalyticsStudyMapFromWidget(widget),
      CHART_ANALYTICS_BY_KEY as Record<string, ChartAnalyticsDefinition>,
      {
        symbol,
        exchange: exchangeEnum,
        timeframeSeconds: timeframe,
        ...computeSeriesBackfillWindow(visibleRange, timeframe, nowSec),
      },
      (studyId) => {
        try {
          const values = chart.getStudyById(studyId).getInputValues?.()
          if (!values) return null
          return Object.fromEntries(values.map((item) => [item.id, item.value]))
        } catch {
          return null
        }
      }
    )
    datafeed.seriesManager.syncActiveStudies(entries)
    datafeed.seriesManager.extendActiveWindows(entries)
  }, [exchangeEnum, symbol, timeframe, widgetRef])

  // Mirrors so the symbol/exchange-change effect below can call the latest
  // versions without listing them as deps — both are recreated on every
  // timeframe change (see their own dep arrays above), and including them
  // there previously caused that effect to re-run — and re-issue setSymbol +
  // resetData — on every timeframe switch too, racing the timeframe-change
  // effect's own reset and wiping out the bar it had just built.
  const syncActiveAnalyticsFromChartRef = useRef(syncActiveAnalyticsFromChart)
  syncActiveAnalyticsFromChartRef.current = syncActiveAnalyticsFromChart
  const syncSeriesSubscriptionsRef = useRef(syncSeriesSubscriptions)
  syncSeriesSubscriptionsRef.current = syncSeriesSubscriptions

  const onToggleIndicator = useCallback(
    (id: string, active: boolean) => {
      if (
        id === 'volumeDeltaProfile' ||
        id === 'liquidationProfile' ||
        id === 'openInterestProfile'
      ) {
        const key =
          id === 'volumeDeltaProfile'
            ? 'volumeDeltaProfileEnabled'
            : id === 'liquidationProfile'
              ? 'liquidationProfileEnabled'
              : 'openInterestProfileEnabled'
        setChartOption((current) => ({ ...current, [key]: active }))
        syncConfig(cardId, { [key]: String(active) })
        return
      }

      if (id === ORDERBOOK_HEATMAP_INDICATOR_ID) {
        setChartOption((current) => ({ ...current, orderbookHeatmapEnabled: active }))
        syncConfig(cardId, { orderbookHeatmapEnabled: String(active) })
        return
      }

      if (id === 'liquidationLevelProfile' || id === 'stopLevelProfile') {
        const key =
          id === 'liquidationLevelProfile'
            ? 'liquidationLevelProfileEnabled'
            : 'stopLevelProfileEnabled'
        setChartOption((current) => ({ ...current, [key]: active }))
        syncConfig(cardId, { [key]: String(active) })
        return
      }

      if (!widgetRef.current) return
      const chart = widgetRef.current.activeChart()
      const studyMap = getAnalyticsStudyMapFromWidget(widgetRef.current)
      const existingStudyIds = studyMap.get(id) ?? []

      // TV built-in indicators (tv-sma, tv-ema, etc.)
      const tvBuiltin = CHART_BUILTIN_STUDY_NAMES[id]
      if (tvBuiltin) {
        if (active) {
          chart.createStudy(tvBuiltin.tvName, tvBuiltin.isOverlay, false).catch(() => {})
        } else {
          const lastStudyId = existingStudyIds.at(-1)
          if (!lastStudyId) return
          try {
            chart.removeEntity(lastStudyId)
          } catch {}
        }
        return
      }

      // OrderX custom analytics
      const metric = id as ChartAnalyticsMetric
      const definition = CHART_ANALYTICS_DEFINITIONS.find((item) => item.key === metric)

      if (active) {
        if (metric === 'buySellLiq') {
          chart.createStudy('OrderX Liquidation Split', false, false).catch(() => {})
          return
        }
        if (metric === 'netBuyTradeCount') {
          chart.createStudy('OrderX Trade Count (Buy/Sell)', false, false).catch(() => {})
          return
        }
        if (!definition) return
        const studyOptions =
          metric === 'parkinsonVolatility'
            ? { priceScale: 'new-left' }
            : MAIN_PANE_VOLUME_METRICS.includes(metric as (typeof MAIN_PANE_VOLUME_METRICS)[number])
              ? { priceScale: 'no-scale' }
              : undefined
        chart
          .createStudy(
            `OrderX ${definition.label}`,
            definition.placement === 'overlay',
            false,
            undefined,
            undefined,
            studyOptions
          )
          .catch(() => {})
      } else {
        const lastStudyId = existingStudyIds.at(-1)
        if (!lastStudyId) return
        try {
          chart.removeEntity(lastStudyId)
        } catch {}
      }
    },
    [cardId, setChartOption, syncConfig, widgetRef]
  )

  const onRemoveIndicatorInstance = useCallback(
    (instance: ActiveIndicatorInstance) => {
      // Profiles are chart-option flags; the flag drives the study, not the reverse.
      if (SINGLE_INSTANCE_INDICATOR_IDS.has(instance.indicatorId)) {
        onToggleIndicator(instance.indicatorId, false)
        return
      }
      try {
        widgetRef.current?.activeChart().removeEntity(instance.studyId)
      } catch {}
    },
    [onToggleIndicator, widgetRef]
  )

  const refreshIndicatorInstances = useCallback(() => {
    setIndicatorInstances(getActiveIndicatorInstancesFromWidget(widgetRef.current))
  }, [widgetRef])

  /** Runs a study edit, then re-reads the instances so the picker shows the new state. */
  const editStudy = useCallback(
    (studyId: TVEntityId, edit: (study: ReturnType<TVChartApi['getStudyById']>) => void) => {
      const widget = widgetRef.current
      if (!widget) return
      try {
        edit(widget.activeChart().getStudyById(studyId))
      } catch {}
      refreshIndicatorInstances()
    },
    [refreshIndicatorInstances, widgetRef]
  )

  const onToggleIndicatorInstanceVisibility = useCallback(
    (instance: ActiveIndicatorInstance) =>
      editStudy(instance.studyId, (study) => study.setVisible(!instance.visible)),
    [editStudy]
  )

  // TV's own dialog lives inside the chart iframe; the side panel stays open next to it.
  const onOpenIndicatorInstanceSettings = useCallback(
    (instance: ActiveIndicatorInstance) => {
      try {
        widgetRef.current?.activeChart().showPropertiesDialog(instance.studyId)
      } catch {}
    },
    [widgetRef]
  )

  const indicatorSettingsApi = useMemo<IndicatorSettingsApi>(
    () => ({
      read: (studyId) => {
        const widget = widgetRef.current
        return widget ? readIndicatorInstanceSettings(widget.activeChart(), studyId) : null
      },
      setInput: (studyId, inputId, value) =>
        editStudy(studyId, (study) => study.setInputValues([{ id: inputId, value }])),
      setPlot: (studyId, plotKey, patch) => {
        const overrides: Record<string, unknown> = {}
        if (patch.color !== undefined) overrides[`${plotKey}.color`] = patch.color
        if (patch.visible !== undefined) {
          overrides[`${plotKey}.display`] = patch.visible ? PLOT_DISPLAY_ALL : PLOT_DISPLAY_NONE
        }
        editStudy(studyId, (study) => study.applyOverrides(overrides))
      },
    }),
    [editStudy, widgetRef]
  )

  const activeIndicatorIds = useMemo(
    () => [
      ...activeAnalytics,
      ...(volumeDeltaProfileEnabled
        ? (['volumeDeltaProfile'] satisfies VisibleRangeProfileId[])
        : []),
      ...(liquidationProfileEnabled
        ? (['liquidationProfile'] satisfies VisibleRangeProfileId[])
        : []),
      ...(openInterestProfileEnabled
        ? (['openInterestProfile'] satisfies VisibleRangeProfileId[])
        : []),
      ...(liquidationLevelProfileEnabled
        ? (['liquidationLevelProfile'] satisfies LevelProfileId[])
        : []),
      ...(stopLevelProfileEnabled ? (['stopLevelProfile'] satisfies LevelProfileId[]) : []),
      ...(orderbookHeatmapEnabled ? [ORDERBOOK_HEATMAP_INDICATOR_ID] : []),
    ],
    [
      activeAnalytics,
      orderbookHeatmapEnabled,
      liquidationLevelProfileEnabled,
      liquidationProfileEnabled,
      openInterestProfileEnabled,
      stopLevelProfileEnabled,
      volumeDeltaProfileEnabled,
    ]
  )
  const activeVisibleRangeProfiles = useMemo(
    () => [
      ...(volumeDeltaProfileEnabled ? (['volume'] as const) : []),
      ...(liquidationProfileEnabled ? (['liquidation'] as const) : []),
      ...(openInterestProfileEnabled ? (['openInterest'] as const) : []),
    ],
    [liquidationProfileEnabled, openInterestProfileEnabled, volumeDeltaProfileEnabled]
  )
  const activeLevelProfiles = useMemo(
    () => [
      ...(liquidationLevelProfileEnabled ? (['liquidationLevels'] as const) : []),
      ...(stopLevelProfileEnabled ? (['stopLevels'] as const) : []),
    ],
    [liquidationLevelProfileEnabled, stopLevelProfileEnabled]
  )

  const syncVisibleRangeProfileFromStudy = useCallback(
    (kind: VisibleRangeProfileKind, studyId: TVEntityId | null) => {
      const { enabledKey, unitKey, showLabelsKey } = VISIBLE_RANGE_PROFILE_OPTION_KEYS[kind]
      const enabled = studyId !== null
      let unit: VisibleRangeProfileUnit | undefined
      let showLabels: boolean | undefined

      if (studyId) {
        try {
          const inputValues = widgetRef.current
            ?.activeChart()
            .getStudyById(studyId)
            .getInputValues?.()
          const displaySettings = readVisibleRangeProfileDisplaySettings(inputValues)
          setVisibleRangeProfileSettingsByKind((current) =>
            areVisibleRangeProfileSettingsEqual(current[kind], displaySettings)
              ? current
              : { ...current, [kind]: displaySettings }
          )
          const value = inputValues?.find(
            (input) => input.id === VISIBLE_RANGE_PROFILE_UNIT_INPUT_ID
          )?.value
          if (value === 'base' || value === 'quote') unit = value
          const showLabelsValue = inputValues?.find(
            (input) => input.id === PROFILE_SHOW_LABELS_INPUT_ID
          )?.value
          if (typeof showLabelsValue === 'boolean') showLabels = showLabelsValue
          else if (showLabelsValue === 'true' || showLabelsValue === 'false') {
            showLabels = showLabelsValue === 'true'
          }
        } catch {}
      }

      const visibleRangeProfileCurrentStateByKind: Record<
        VisibleRangeProfileKind,
        { enabled: boolean; unit: VisibleRangeProfileUnit; showLabels: boolean }
      > = {
        volume: {
          enabled: volumeDeltaProfileEnabled,
          unit: volumeDeltaProfileUnit,
          showLabels: volumeDeltaProfileShowLabels,
        },
        liquidation: {
          enabled: liquidationProfileEnabled,
          unit: liquidationProfileUnit,
          showLabels: liquidationProfileShowLabels,
        },
        openInterest: {
          enabled: openInterestProfileEnabled,
          unit: openInterestProfileUnit,
          showLabels: openInterestProfileShowLabels,
        },
      }
      const {
        enabled: currentEnabled,
        unit: currentUnit,
        showLabels: currentShowLabels,
      } = visibleRangeProfileCurrentStateByKind[kind]
      if (
        currentEnabled === enabled &&
        (!unit || currentUnit === unit) &&
        (showLabels === undefined || currentShowLabels === showLabels)
      ) {
        return
      }

      setChartOption((current) => ({
        ...current,
        [enabledKey]: enabled,
        ...(unit ? { [unitKey]: unit } : {}),
        ...(showLabels !== undefined ? { [showLabelsKey]: showLabels } : {}),
      }))
      syncConfig(cardId, {
        [enabledKey]: String(enabled),
        ...(unit ? { [unitKey]: unit } : {}),
        ...(showLabels !== undefined ? { [showLabelsKey]: String(showLabels) } : {}),
      })
    },
    [
      cardId,
      liquidationProfileEnabled,
      liquidationProfileShowLabels,
      liquidationProfileUnit,
      openInterestProfileEnabled,
      openInterestProfileShowLabels,
      openInterestProfileUnit,
      setChartOption,
      syncConfig,
      volumeDeltaProfileEnabled,
      volumeDeltaProfileShowLabels,
      volumeDeltaProfileUnit,
      widgetRef,
    ]
  )

  useEffect(() => {
    if (!isChartReady || !widgetRef.current) return

    const chart = widgetRef.current.activeChart()
    const studies = chart.getAllStudies()
    const profiles: Array<{
      kind: VisibleRangeProfileKind
      enabled: boolean
      unit: VisibleRangeProfileUnit
      showLabels: boolean
      displaySettings: VisibleRangeProfileDisplaySettings
    }> = [
      {
        kind: 'volume',
        enabled: volumeDeltaProfileEnabled,
        unit: volumeDeltaProfileUnit,
        showLabels: volumeDeltaProfileShowLabels,
        displaySettings: visibleRangeProfileSettingsByKind.volume,
      },
      {
        kind: 'liquidation',
        enabled: liquidationProfileEnabled,
        unit: liquidationProfileUnit,
        showLabels: liquidationProfileShowLabels,
        displaySettings: visibleRangeProfileSettingsByKind.liquidation,
      },
      {
        kind: 'openInterest',
        enabled: openInterestProfileEnabled,
        unit: openInterestProfileUnit,
        showLabels: openInterestProfileShowLabels,
        displaySettings: visibleRangeProfileSettingsByKind.openInterest,
      },
    ]

    profiles.forEach(({ kind, enabled, unit, showLabels, displaySettings }) => {
      const definition = VISIBLE_RANGE_PROFILE_STUDIES[kind]
      const { primary: existing, duplicates } = selectSingleNamedStudy(
        studies,
        definition.name,
        visibleRangeProfileStudyIdsRef.current[kind]
      )

      if (!enabled) {
        for (const study of existing ? [existing, ...duplicates] : duplicates) {
          try {
            chart.removeEntity(study.id)
          } catch {}
        }
        delete visibleRangeProfileStudyIdsRef.current[kind]
        return
      }

      if (existing) {
        visibleRangeProfileStudyIdsRef.current[kind] = existing.id
        for (const duplicate of duplicates) {
          try {
            chart.removeEntity(duplicate.id)
          } catch {}
        }

        try {
          const studyApi = chart.getStudyById(existing.id)
          const inputValues = studyApi.getInputValues?.()
          const currentUnit = inputValues?.find(
            (input) => input.id === VISIBLE_RANGE_PROFILE_UNIT_INPUT_ID
          )?.value
          const currentShowLabels = inputValues?.find(
            (input) => input.id === PROFILE_SHOW_LABELS_INPUT_ID
          )?.value
          const restoredDisplaySettings = readVisibleRangeProfileDisplaySettings(inputValues)
          setVisibleRangeProfileSettingsByKind((current) =>
            areVisibleRangeProfileSettingsEqual(current[kind], restoredDisplaySettings)
              ? current
              : { ...current, [kind]: restoredDisplaySettings }
          )
          const nextInputs: Array<{ id: string; value: string | boolean }> = []
          if (currentUnit !== unit) {
            nextInputs.push({ id: VISIBLE_RANGE_PROFILE_UNIT_INPUT_ID, value: unit })
          }
          if (currentShowLabels !== showLabels) {
            nextInputs.push({ id: PROFILE_SHOW_LABELS_INPUT_ID, value: showLabels })
          }
          if (nextInputs.length > 0) studyApi.setInputValues(nextInputs)
        } catch {}
        return
      }

      delete visibleRangeProfileStudyIdsRef.current[kind]
      if (!enabled || visibleRangeProfileStudyCreationRef.current[kind]) return

      const creation = markProgrammaticStudyCreate(
        chart.createStudy(definition.name, true, false, {
          [VISIBLE_RANGE_PROFILE_UNIT_INPUT_ID]: unit,
          [VISIBLE_RANGE_PROFILE_ROWS_LAYOUT_INPUT_ID]: displaySettings.rowsLayout,
          [VISIBLE_RANGE_PROFILE_ROW_SIZE_INPUT_ID]: displaySettings.rowSize,
          [VISIBLE_RANGE_PROFILE_VOLUME_MODE_INPUT_ID]: displaySettings.volumeMode,
          [VISIBLE_RANGE_PROFILE_VALUE_AREA_INPUT_ID]: displaySettings.valueAreaPercent,
          [PROFILE_SHOW_LABELS_INPUT_ID]: showLabels,
        })
      )
      visibleRangeProfileStudyCreationRef.current[kind] = creation
      creation
        .then((studyId) => {
          if (!studyId) return
          if (!visibleRangeProfileEnabledRef.current[kind]) {
            try {
              chart.removeEntity(studyId)
            } catch {}
            return
          }
          visibleRangeProfileStudyIdsRef.current[kind] = studyId
        })
        .catch(() => {})
        .finally(() => {
          if (visibleRangeProfileStudyCreationRef.current[kind] === creation) {
            delete visibleRangeProfileStudyCreationRef.current[kind]
          }
        })
    })
  }, [
    isChartReady,
    liquidationProfileEnabled,
    liquidationProfileShowLabels,
    liquidationProfileUnit,
    openInterestProfileEnabled,
    openInterestProfileShowLabels,
    openInterestProfileUnit,
    volumeDeltaProfileEnabled,
    volumeDeltaProfileShowLabels,
    volumeDeltaProfileUnit,
    visibleRangeProfileSettingsByKind,
    widgetRef,
  ])

  const syncLevelProfileFromStudy = useCallback(
    (kind: LevelProfileKind, studyId: TVEntityId | null) => {
      const { enabledKey, measureKey, showLabelsKey } = LEVEL_PROFILE_OPTION_KEYS[kind]
      const enabled = studyId !== null
      let measure: LevelProfileMeasure | undefined
      let showLabels: boolean | undefined

      if (studyId) {
        try {
          const inputValues = widgetRef.current
            ?.activeChart()
            .getStudyById(studyId)
            .getInputValues?.()
          const value = inputValues?.find(
            (input) => input.id === LEVEL_PROFILE_MEASURE_INPUT_ID
          )?.value
          if (value === 'notional' || value === 'count') measure = value
          const showLabelsValue = inputValues?.find(
            (input) => input.id === PROFILE_SHOW_LABELS_INPUT_ID
          )?.value
          if (typeof showLabelsValue === 'boolean') showLabels = showLabelsValue
          else if (showLabelsValue === 'true' || showLabelsValue === 'false') {
            showLabels = showLabelsValue === 'true'
          }
        } catch {}
      }

      const levelProfileCurrentStateByKind: Record<
        LevelProfileKind,
        { enabled: boolean; measure: LevelProfileMeasure; showLabels: boolean }
      > = {
        liquidationLevels: {
          enabled: liquidationLevelProfileEnabled,
          measure: liquidationLevelProfileMeasure,
          showLabels: liquidationLevelProfileShowLabels,
        },
        stopLevels: {
          enabled: stopLevelProfileEnabled,
          measure: stopLevelProfileMeasure,
          showLabels: stopLevelProfileShowLabels,
        },
      }
      const {
        enabled: currentEnabled,
        measure: currentMeasure,
        showLabels: currentShowLabels,
      } = levelProfileCurrentStateByKind[kind]
      if (
        currentEnabled === enabled &&
        (!measure || currentMeasure === measure) &&
        (showLabels === undefined || currentShowLabels === showLabels)
      ) {
        return
      }

      setChartOption((current) => ({
        ...current,
        [enabledKey]: enabled,
        ...(measure ? { [measureKey]: measure } : {}),
        ...(showLabels !== undefined ? { [showLabelsKey]: showLabels } : {}),
      }))
      syncConfig(cardId, {
        [enabledKey]: String(enabled),
        ...(measure ? { [measureKey]: measure } : {}),
        ...(showLabels !== undefined ? { [showLabelsKey]: String(showLabels) } : {}),
      })
    },
    [
      cardId,
      liquidationLevelProfileEnabled,
      liquidationLevelProfileMeasure,
      liquidationLevelProfileShowLabels,
      setChartOption,
      stopLevelProfileEnabled,
      stopLevelProfileMeasure,
      stopLevelProfileShowLabels,
      syncConfig,
      widgetRef,
    ]
  )

  useEffect(() => {
    if (!isChartReady || !widgetRef.current) return

    const chart = widgetRef.current.activeChart()
    const studies = chart.getAllStudies()
    const profiles: Array<{
      kind: LevelProfileKind
      enabled: boolean
      measure: LevelProfileMeasure
      showLabels: boolean
    }> = [
      {
        kind: 'liquidationLevels',
        enabled: liquidationLevelProfileEnabled,
        measure: liquidationLevelProfileMeasure,
        showLabels: liquidationLevelProfileShowLabels,
      },
      {
        kind: 'stopLevels',
        enabled: stopLevelProfileEnabled,
        measure: stopLevelProfileMeasure,
        showLabels: stopLevelProfileShowLabels,
      },
    ]

    profiles.forEach(({ kind, enabled, measure, showLabels }) => {
      const definition = LEVEL_PROFILE_STUDIES[kind]
      const { primary: existing, duplicates } = selectSingleNamedStudy(
        studies,
        definition.name,
        levelProfileStudyIdsRef.current[kind]
      )

      if (!enabled) {
        for (const study of existing ? [existing, ...duplicates] : duplicates) {
          try {
            chart.removeEntity(study.id)
          } catch {}
        }
        delete levelProfileStudyIdsRef.current[kind]
        return
      }

      if (existing) {
        levelProfileStudyIdsRef.current[kind] = existing.id
        for (const duplicate of duplicates) {
          try {
            chart.removeEntity(duplicate.id)
          } catch {}
        }

        try {
          const studyApi = chart.getStudyById(existing.id)
          const inputValues = studyApi.getInputValues?.()
          const currentMeasure = inputValues?.find(
            (input) => input.id === LEVEL_PROFILE_MEASURE_INPUT_ID
          )?.value
          const currentShowLabels = inputValues?.find(
            (input) => input.id === PROFILE_SHOW_LABELS_INPUT_ID
          )?.value
          const nextInputs: Array<{ id: string; value: string | boolean }> = []
          if (currentMeasure !== measure) {
            nextInputs.push({ id: LEVEL_PROFILE_MEASURE_INPUT_ID, value: measure })
          }
          if (currentShowLabels !== showLabels) {
            nextInputs.push({ id: PROFILE_SHOW_LABELS_INPUT_ID, value: showLabels })
          }
          if (nextInputs.length > 0) studyApi.setInputValues(nextInputs)
        } catch {}
        return
      }

      delete levelProfileStudyIdsRef.current[kind]
      if (!enabled || levelProfileStudyCreationRef.current[kind]) return

      const creation = markProgrammaticStudyCreate(
        chart.createStudy(definition.name, true, false, {
          [LEVEL_PROFILE_MEASURE_INPUT_ID]: measure,
          [PROFILE_SHOW_LABELS_INPUT_ID]: showLabels,
        })
      )
      levelProfileStudyCreationRef.current[kind] = creation
      creation
        .then((studyId) => {
          if (!studyId) return
          if (!levelProfileEnabledRef.current[kind]) {
            try {
              chart.removeEntity(studyId)
            } catch {}
            return
          }
          levelProfileStudyIdsRef.current[kind] = studyId
        })
        .catch(() => {})
        .finally(() => {
          if (levelProfileStudyCreationRef.current[kind] === creation) {
            delete levelProfileStudyCreationRef.current[kind]
          }
        })
    })
  }, [
    isChartReady,
    liquidationLevelProfileEnabled,
    liquidationLevelProfileMeasure,
    liquidationLevelProfileShowLabels,
    stopLevelProfileEnabled,
    stopLevelProfileMeasure,
    stopLevelProfileShowLabels,
    widgetRef,
  ])

  // The study's Price Range, Price Grouping and Min/Max Size inputs → chartOption (shared with the Percept
  // heatmap) + server config.
  const syncOrderbookHeatmapRangeFromStudy = useCallback(
    (studyId: TVEntityId) => {
      let inputs: { id: string; value: unknown }[] | undefined
      try {
        inputs = widgetRef.current?.activeChart().getStudyById(studyId).getInputValues?.()
      } catch {}
      if (!inputs) return
      const valueOf = (id: string) => inputs?.find((input) => input.id === id)?.value
      const range = parseOrderbookHeatmapRange(valueOf(ORDERBOOK_HEATMAP_RANGE_INPUT_ID))
      const grouping = parseOrderbookHeatmapGrouping(valueOf(ORDERBOOK_HEATMAP_GROUPING_INPUT_ID))
      const minSize = parseOrderbookHeatmapSize(valueOf(ORDERBOOK_HEATMAP_MIN_SIZE_INPUT_ID))
      const maxSize = parseOrderbookHeatmapSize(valueOf(ORDERBOOK_HEATMAP_MAX_SIZE_INPUT_ID))
      const rangeChanged = range !== undefined && range !== orderbookHeatmapRangeRef.current
      const groupingChanged =
        grouping !== undefined && grouping !== orderbookHeatmapGroupingRef.current
      const sizeFilter = orderbookHeatmapSizeFilterRef.current
      const sizeChanged = minSize !== sizeFilter.min || maxSize !== sizeFilter.max
      if (!rangeChanged && !groupingChanged && !sizeChanged) return
      setChartOption((current) => ({
        ...current,
        ...(rangeChanged ? { orderbookHeatmapRange: range } : {}),
        ...(groupingChanged ? { orderbookHeatmapGrouping: grouping } : {}),
        ...(sizeChanged
          ? { orderbookHeatmapMinSize: minSize, orderbookHeatmapMaxSize: maxSize }
          : {}),
      }))
      syncConfig(cardId, {
        ...(rangeChanged ? { orderbookHeatmapRange: String(range) } : {}),
        ...(groupingChanged ? { orderbookHeatmapGrouping: String(grouping) } : {}),
        ...(sizeChanged
          ? {
              orderbookHeatmapMinSize: String(minSize),
              orderbookHeatmapMaxSize: String(maxSize),
            }
          : {}),
      })
    },
    [cardId, setChartOption, syncConfig, widgetRef]
  )

  // chartOption → the study's Min/Max inputs, so a change from the chart Settings menu (or the
  // Percept heatmap) shows up in the study dialog and isn't overwritten by its stale values.
  useEffect(() => {
    const studyId = heatmapStudyIdRef.current
    if (!isChartReady || !studyId) return
    try {
      const study = widgetRef.current?.activeChart().getStudyById(studyId)
      const inputs = study?.getInputValues?.() ?? []
      const valueOf = (id: string) => inputs.find((input) => input.id === id)?.value
      const next = [
        { id: ORDERBOOK_HEATMAP_MIN_SIZE_INPUT_ID, size: orderbookHeatmapSizeFilter.min },
        { id: ORDERBOOK_HEATMAP_MAX_SIZE_INPUT_ID, size: orderbookHeatmapSizeFilter.max },
      ]
        .filter(({ id, size }) => parseOrderbookHeatmapSize(valueOf(id)) !== size)
        .map(({ id, size }) => ({ id, value: formatOrderbookHeatmapSize(size) }))
      if (
        parseOrderbookHeatmapGrouping(valueOf(ORDERBOOK_HEATMAP_GROUPING_INPUT_ID)) !==
        orderbookHeatmapGrouping
      ) {
        next.push({
          id: ORDERBOOK_HEATMAP_GROUPING_INPUT_ID,
          value: String(orderbookHeatmapGrouping),
        })
      }
      if (next.length > 0) study?.setInputValues(next)
    } catch {}
  }, [isChartReady, orderbookHeatmapGrouping, orderbookHeatmapSizeFilter, widgetRef])

  const syncOrderbookHeatmapFromStudy = useCallback(
    (studyId: TVEntityId | null) => {
      if (studyId) syncOrderbookHeatmapRangeFromStudy(studyId)
      const enabled = studyId !== null
      // Compared against the gated flag, so the study being removed while unsupported (options,
      // log scale, monthly) leaves the saved toggle alone.
      // Read through the ref so a toggle doesn't rebuild the study subscription this feeds.
      if (enabled === heatmapEnabledRef.current) return
      setChartOption((current) => ({ ...current, orderbookHeatmapEnabled: enabled }))
      syncConfig(cardId, { orderbookHeatmapEnabled: String(enabled) })
    },
    [cardId, setChartOption, syncConfig, syncOrderbookHeatmapRangeFromStudy]
  )

  useEffect(() => {
    if (!isChartReady || !widgetRef.current) return

    const chart = widgetRef.current.activeChart()
    const { primary: existing, duplicates } = selectSingleNamedStudy(
      chart.getAllStudies(),
      ORDERBOOK_HEATMAP_STUDY.name,
      heatmapStudyIdRef.current ?? undefined
    )

    if (!orderbookHeatmapEnabled) {
      for (const study of existing ? [existing, ...duplicates] : duplicates) {
        try {
          chart.removeEntity(study.id)
        } catch {}
      }
      heatmapStudyIdRef.current = null
      return
    }

    if (existing) {
      heatmapStudyIdRef.current = existing.id
      for (const duplicate of duplicates) {
        try {
          chart.removeEntity(duplicate.id)
        } catch {}
      }
      return
    }

    heatmapStudyIdRef.current = null
    if (heatmapStudyCreationRef.current) return

    const creation = markProgrammaticStudyCreate(
      chart.createStudy(ORDERBOOK_HEATMAP_STUDY.name, true, false, {
        [ORDERBOOK_HEATMAP_RANGE_INPUT_ID]: String(orderbookHeatmapRangeRef.current),
        [ORDERBOOK_HEATMAP_GROUPING_INPUT_ID]: String(orderbookHeatmapGroupingRef.current),
        [ORDERBOOK_HEATMAP_MIN_SIZE_INPUT_ID]: formatOrderbookHeatmapSize(
          orderbookHeatmapSizeFilterRef.current.min
        ),
        [ORDERBOOK_HEATMAP_MAX_SIZE_INPUT_ID]: formatOrderbookHeatmapSize(
          orderbookHeatmapSizeFilterRef.current.max
        ),
      })
    )
    heatmapStudyCreationRef.current = creation
    creation
      .then((studyId) => {
        if (!studyId) return
        if (!heatmapEnabledRef.current) {
          try {
            chart.removeEntity(studyId)
          } catch {}
          return
        }
        heatmapStudyIdRef.current = studyId
      })
      .catch(() => {})
      .finally(() => {
        if (heatmapStudyCreationRef.current === creation) heatmapStudyCreationRef.current = null
      })
  }, [isChartReady, orderbookHeatmapEnabled, widgetRef])

  const getChartLiveTicker = useCallback(
    (sym: string) =>
      sym === symbol && chartLiveMarkPrice ? { markPrice: chartLiveMarkPrice } : undefined,
    [symbol, chartLiveMarkPrice]
  )
  const getOverlayAccountLabel = useCallback(
    (accountId: string) => accountLabelById.get(accountId),
    [accountLabelById]
  )

  const { clearTradingOverlays, loadOrderOverlays, updatePositionPnlDisplay } = useTradingOverlays({
    widgetRef,
    datafeedRef,
    tradesRef,
    positionsRef,
    openOrdersRef,
    scaleLevelsRef,
    priceTickSize: symbolTickSize,
    getLiveTicker: getChartLiveTicker,
    baseCurrency: resolveBaseCurrency([], symbol, symbolInstrument),
    positionSizeUnit: chartOption?.positionSizeUnit ?? 'base',
    showPositionLine: chartOption?.showPositionLine ?? true,
    showLiquidationLine: chartOption?.showLiquidationLine ?? chartOption?.showPositionLine ?? true,
    showBuySellTriangles: chartOption?.showBuySellTriangles ?? true,
    showBuySellButtons: chartOption?.showBuySellButtons ?? true,
    showOpenOrders: chartOption?.showOpenOrders ?? true,
    getAccountLabel: getOverlayAccountLabel,
    accountIds: chartAccountIds,
    exchange: exchangeEnum,
  })

  // 5. Callbacks
  const onPriceUpdate = useCallback((price: number) => {
    livePriceRef.current = price
    if (livePriceCommitTimerRef.current) return
    livePriceCommitTimerRef.current = setTimeout(() => {
      livePriceCommitTimerRef.current = null
      setLivePrice(livePriceRef.current)
    }, LIVE_PRICE_COMMIT_MS)
  }, [])

  useEffect(
    () => () => {
      if (livePriceCommitTimerRef.current) clearTimeout(livePriceCommitTimerRef.current)
    },
    []
  )

  // Shared with the Percept chart's Buy/Sell buttons (useChartOrderFormAppend).
  const openLimitOrderFormAtPrice = useChartOrderFormAppend({
    cardId,
    symbol,
    exchange: exchangeEnum,
    symbolInstrument,
    lastPrice: symbolLastPrice ?? livePrice.toString(),
    anchorRef: containerRef,
  })

  openLimitOrderFormAtPriceRef.current = openLimitOrderFormAtPrice

  const onBarClose = useCallback(() => {
    datafeedRef.current?.seriesManager.notifyBarClose()
  }, [])

  // Custom hook needing onPriceUpdate (after callback)
  useChartRealtimeBridge({
    datafeedRef,
    isChartReady,
    enabled: realtimeEnabled,
    symbol,
    exchangeEnum,
    timeframe,
    lastPushedBarTimeRef,
    onPriceUpdate,
    onBarClose,
  })
  useComparisonRealtimeBridge({ datafeedRef, isChartReady, enabled: realtimeEnabled })

  useEffect(() => {
    if (previousCompareLinkingColorRef.current === compareLinkingColor) return
    previousCompareLinkingColorRef.current = compareLinkingColor
    // A command belongs to the linking group that existed when it was emitted.
    // Baseline the newly joined group so its retained command cannot replay here.
    lastHandledCompareRequestIdRef.current = compareCommand?.requestId ?? null
  }, [compareCommand?.requestId, compareLinkingColor])

  useEffect(() => {
    if (!isChartReady || !widgetRef.current) return
    const chart = widgetRef.current.activeChart()
    const hasCompareStudy = chart.getAllStudies().some((study) => study.name === 'Compare')
    if (hasCompareStudy) compareScaleInitializedRef.current = true
    applyCompareStudyPalette(chart)
    syncCompareSymbolLabels(widgetRef.current)
  }, [isChartReady, widgetRef])

  useEffect(() => {
    if (!isChartReady) return

    patchMeasureLegendLabels()
    const iframe = containerRef.current?.querySelector('iframe')
    const iframeDocument = iframe?.contentDocument
    const observer = iframeDocument?.body
      ? new MutationObserver((mutations) => {
          const hasUnqualifiedLabel = mutations.some((mutation) => {
            if (mutation.type === 'characterData') {
              return containsUnqualifiedOrderXMeasureLabel(mutation.target)
            }
            return Array.from(mutation.addedNodes).some(containsUnqualifiedOrderXMeasureLabel)
          })
          if (hasUnqualifiedLabel) patchMeasureLegendLabels()
        })
      : null
    observer?.observe(iframeDocument!.body, {
      childList: true,
      characterData: true,
      subtree: true,
    })
    const timeoutIds = [100, 300, 750, 1500].map((delay) =>
      setTimeout(patchMeasureLegendLabels, delay)
    )
    return () => {
      observer?.disconnect()
      timeoutIds.forEach(clearTimeout)
    }
  }, [
    containerRef,
    isChartReady,
    liquidationProfileEnabled,
    openInterestProfileEnabled,
    patchMeasureLegendLabels,
    volumeDeltaProfileEnabled,
  ])

  useEffect(() => {
    if (!isChartReady || !widgetRef.current || !compareCommand) return
    if (
      !shouldProcessCompareCommand(compareCommand, lastHandledCompareRequestIdRef.current, cardId)
    ) {
      return
    }
    lastHandledCompareRequestIdRef.current = compareCommand.requestId

    let cancelled = false
    const chart = widgetRef.current.activeChart()
    // 'percent' lands on the main % scale as a `Compare` study; the other two
    // modes are an `Overlay` study on its own price scale, optionally moved
    // into a new pane — the same three choices as TradingView's compare dialog.
    const compareMode = compareCommand.mode ?? 'percent'
    const isPercentCompare = compareMode === 'percent'
    const compareStudyName = isPercentCompare ? 'Compare' : 'Overlay'
    const existingSymbols = new Set(
      chart
        .getAllStudies()
        .filter((study) => study.name === compareStudyName)
        .map((study) => getCompareStudySymbol(chart, study.id))
        .filter((value): value is string => Boolean(value))
    )
    const existingCompareCount = existingSymbols.size
    if (existingCompareCount > 0) compareScaleInitializedRef.current = true
    const mainSymbol = qualifyChartSymbol({ exchange: exchangeEnum, symbol })

    const addComparisons = async () => {
      // Dedupe up front (against the main symbol, existing studies, AND
      // duplicates within this same batch) so the color index below can be
      // precomputed from batch position instead of a running "created so far"
      // counter — that's what lets creation go concurrent, since it no longer
      // needs to observe previous iterations' results to know its own color.
      const seen = new Set(existingSymbols)
      const toCreate: typeof compareCommand.symbols = []
      let skipped = 0
      for (const identity of compareCommand.symbols) {
        const qualifiedSymbol = qualifyChartSymbol(identity)
        if (qualifiedSymbol === mainSymbol || seen.has(qualifiedSymbol)) {
          skipped += 1
          continue
        }
        seen.add(qualifiedSymbol)
        toCreate.push(identity)
      }

      let created = 0
      let failed = 0
      try {
        // Fired concurrently — an N-symbol compare no longer pays N sequential
        // network round trips (each createStudy internally resolves the
        // symbol). Promise.allSettled so one failure doesn't cancel the rest.
        const outcomes = await Promise.allSettled(
          toCreate.map((identity, index) => {
            const qualifiedSymbol = qualifyChartSymbol(identity)
            if (!isPercentCompare) {
              return markProgrammaticStudyCreate(
                chart.createStudy('Overlay', true, false, { symbol: qualifiedSymbol }, undefined, {
                  priceScale: 'new-right',
                })
              ).then((studyId) => {
                if (studyId && compareMode === 'new-pane') {
                  chart.getStudyById(studyId).unmergeDown()
                }
                return studyId
              })
            }
            return markProgrammaticStudyCreate(
              chart.createStudy(
                'Compare',
                true,
                false,
                {
                  symbol: qualifiedSymbol,
                },
                {
                  'plot.color': getCompareSeriesColor(existingCompareCount + index),
                  'plot.linewidth': 2,
                  'plot.transparency': 0,
                }
              )
            )
          })
        )

        outcomes.forEach((outcome, index) => {
          if (outcome.status === 'fulfilled' && outcome.value) {
            created += 1
            return
          }
          failed += 1
          if (outcome.status === 'rejected') {
            console.warn(
              `[TVChartWidget] Failed to compare ${qualifyChartSymbol(toCreate[index])}:`,
              outcome.reason
            )
          }
        })

        // The effect was cleaned up (unmount / superseded command) while the
        // concurrent creates were in flight — createStudy calls that already
        // landed can't be un-sent, but the chart/widget may be gone, so treat
        // them as failed rather than touching the UI below.
        if (cancelled) {
          failed += created
          created = 0
        }

        if (created > 0 && isPercentCompare) {
          applyCompareStudyPalette(chart)
          syncCompareSymbolLabels(widgetRef.current)
        }

        if (
          !cancelled &&
          isPercentCompare &&
          shouldInitializeCompareScale(
            compareScaleInitializedRef.current,
            existingCompareCount,
            created
          )
        ) {
          compareScaleInitializedRef.current = true
          try {
            getMainPriceScale()?.setMode(PRICE_SCALE_MODE_MAP.Percentage)
            getMainPriceScale()?.setAutoScale(true)
            widgetRef.current?.executeActionById('Chart.PriceScale.Reset')
          } catch {}
          setChartOption((current) => ({ ...current, scale: 'Percentage', autoScale: true }))
          syncConfig(cardId, { scale: 'Percentage', autoScale: 'true' })
        }
      } finally {
        acknowledgeCompareCommand(compareCommand.requestId, cardId, { created, skipped, failed })
      }
    }

    void addComparisons()
    return () => {
      cancelled = true
    }
  }, [
    acknowledgeCompareCommand,
    cardId,
    compareCommand,
    exchangeEnum,
    getMainPriceScale,
    isChartReady,
    setChartOption,
    symbol,
    syncConfig,
    widgetRef,
  ])

  useEffect(() => {
    if (!isChartReady || !widgetRef.current || !isCompareUniverseResolved) return
    const chart = widgetRef.current.activeChart()
    const primaryIdentity = { exchange: exchangeEnum, symbol }
    let removedCompare = false
    chart.getAllStudies().forEach((study) => {
      if (study.name !== 'Compare') return
      const qualifiedSymbol = getCompareStudySymbol(chart, study.id)
      const identity = qualifiedSymbol ? parseQualifiedChartSymbol(qualifiedSymbol) : null
      if (!shouldRemoveCompareStudy(identity, compareAllowedExchanges, primaryIdentity)) return
      try {
        chart.removeEntity(study.id)
        removedCompare = true
      } catch {}
    })
    if (!removedCompare) return
    syncCompareSymbolLabels(widgetRef.current)
    const syncTimeoutId = setTimeout(() => syncCompareSymbolLabels(widgetRef.current), 0)
    return () => clearTimeout(syncTimeoutId)
  }, [
    compareAllowedExchanges,
    exchangeEnum,
    isChartReady,
    isCompareUniverseResolved,
    symbol,
    widgetRef,
  ])

  useEffect(() => {
    // When an own-fetch backfill/refresh lands, bump the hidden refreshNonce on
    // every study of the updated metrics so TV recomputes them over history
    // (B2 Q1 primary). setInputValues fires study_properties_changed; the
    // autosave handler strips refreshNonce from its content diff, so a bump is
    // a harmless no-op there (no time-window suppression needed).
    if (!isChartReady) return
    const datafeed = datafeedRef.current
    const widget = widgetRef.current
    if (!datafeed || !widget) return

    datafeed.seriesManager.setOnSeriesUpdated((_sourceId, metrics, isFirstData) => {
      // First landing of a series' history: the study's own main() already
      // ran once, empty, for every already-loaded historical bar before this
      // async fetch could resolve — a nonce bump only recomputes the study's
      // own state and does not reliably force TV to re-walk bars it already
      // computed for these custom (non-Pine) studies. Force the same native
      // reset syncActiveAnalyticsFromChart falls back to for a family-mask
      // widen, debounced so adding several own-fetch studies at once (e.g.
      // all 5 ob-metrics indicators) coalesces into one reset, not one each.
      if (isFirstData) {
        if (firstDataResetTimeoutRef.current) clearTimeout(firstDataResetTimeoutRef.current)
        firstDataResetTimeoutRef.current = setTimeout(() => {
          firstDataResetTimeoutRef.current = null
          try {
            widgetRef.current?.resetCache()
            widgetRef.current?.activeChart().resetData()
          } catch {}
        }, FIRST_DATA_RESET_DEBOUNCE_MS)
        return
      }

      const chart = widget.activeChart()
      const studyMap = getAnalyticsStudyMapFromWidget(widget)
      refreshNonceRef.current = nextRefreshNonce(refreshNonceRef.current)
      for (const metric of metrics) {
        for (const studyId of studyMap.get(metric) ?? []) {
          try {
            chart
              .getStudyById(studyId)
              .setInputValues([{ id: REFRESH_NONCE_INPUT_ID, value: refreshNonceRef.current }])
          } catch {}
        }
      }
    })
    return () => {
      datafeed.seriesManager.setOnSeriesUpdated(null)
      if (firstDataResetTimeoutRef.current) {
        clearTimeout(firstDataResetTimeoutRef.current)
        firstDataResetTimeoutRef.current = null
      }
    }
  }, [isChartReady, widgetRef])

  useEffect(() => {
    if (!isChartReady || !widgetRef.current) return

    const widget = widgetRef.current
    const handlePlusClick = (params: unknown) => {
      const plusParams = params as TVPlusClickParams
      if (typeof plusParams.price !== 'number') return
      lastPlusClickRef.current = { ...plusParams, receivedAt: Date.now() }
    }

    widget.subscribe('onPlusClick', handlePlusClick)

    return () => {
      try {
        widget.unsubscribe('onPlusClick', handlePlusClick)
      } catch {}
    }
  }, [isChartReady, widgetRef])

  // Historical order markers are now handled by useTradingOverlays

  useEffect(() => {
    if (!isChartReady || !widgetRef.current) return

    const widget = widgetRef.current
    const chart = widget.activeChart()
    chart.getAllStudies().forEach((study) => {
      const kind = getVisibleRangeProfileKindByStudyName(study.name)
      if (kind) visibleRangeProfileStudyIdsRef.current[kind] = study.id
      const levelKind = getLevelProfileKindByStudyName(study.name)
      if (levelKind) levelProfileStudyIdsRef.current[levelKind] = study.id
      if (study.name === ORDERBOOK_HEATMAP_STUDY.name) heatmapStudyIdRef.current = study.id
    })

    const handleAutoSave = () => {
      if (!widgetRef.current) return
      widgetRef.current.save((state) => {
        const sanitizedState = stripRefreshNonce(
          sanitizeTVClientStyleState(state, colorThemeRef.current)
        )
        // Persist drawings to localStorage via atomWithStorage (linking group state)
        if (!isChartStateUnchanged(sanitizedState, chartContentRef.current)) {
          const content = JSON.stringify(sanitizedState)
          setChartOption((prev) => ({ ...prev, content }))
          compressAndZipChartConfig(sanitizedState).then((compressed) => {
            syncConfig(cardId, { content: compressed })
          })
        }
      })
    }

    let timeoutId: ReturnType<typeof setTimeout>
    const newStudyMergeTimeoutIds: ReturnType<typeof setTimeout>[] = []
    const legendPatchTimeoutIds: ReturnType<typeof setTimeout>[] = []
    const scheduleMeasureLegendPatch = () => {
      patchMeasureLegendLabels()
      legendPatchTimeoutIds.push(setTimeout(patchMeasureLegendLabels, 100))
    }

    syncActiveAnalyticsFromChart()
    syncSeriesSubscriptions()
    // Studies restored from saved_data can still be settling into their panes
    // when onChartReady fires, so the merge-up above may run before the pane
    // is attached. A deferred re-sync catches that case.
    const restoreSyncTimeoutId = setTimeout(syncActiveAnalyticsFromChart, 300)

    const drawingHandler = (_sourceId: string, eventType: TVDrawingEventType) => {
      if (eventType !== 'create' && eventType !== 'remove') return
      clearTimeout(timeoutId)
      timeoutId = setTimeout(handleAutoSave, 1500)
    }

    const studySubscribedAt = Date.now()
    const studyHandler = (entityId: string, eventType: TVStudyEventType) => {
      // paste_study creates a study without a 'create' event — it must acquire
      // series subscriptions (and autosave) too. price_scale_changed is noise.
      if (eventType === 'price_scale_changed') return

      if (eventType === 'create' || eventType === 'paste_study') {
        const createdStudy = chart.getAllStudies().find((study) => study.id === entityId)
        const kind = createdStudy ? getVisibleRangeProfileKindByStudyName(createdStudy.name) : null
        if (kind) {
          const { primary, duplicates } = selectSingleNamedStudy(
            chart.getAllStudies(),
            VISIBLE_RANGE_PROFILE_STUDIES[kind].name,
            visibleRangeProfileStudyIdsRef.current[kind]
          )
          if (primary) {
            visibleRangeProfileStudyIdsRef.current[kind] = primary.id
            for (const duplicate of duplicates) {
              try {
                chart.removeEntity(duplicate.id)
              } catch {}
            }
            syncVisibleRangeProfileFromStudy(kind, primary.id)
          }
        }
        const levelKind = createdStudy ? getLevelProfileKindByStudyName(createdStudy.name) : null
        if (levelKind) {
          const { primary, duplicates } = selectSingleNamedStudy(
            chart.getAllStudies(),
            LEVEL_PROFILE_STUDIES[levelKind].name,
            levelProfileStudyIdsRef.current[levelKind]
          )
          if (primary) {
            levelProfileStudyIdsRef.current[levelKind] = primary.id
            for (const duplicate of duplicates) {
              try {
                chart.removeEntity(duplicate.id)
              } catch {}
            }
            syncLevelProfileFromStudy(levelKind, primary.id)
          }
        }
        if (createdStudy?.name === ORDERBOOK_HEATMAP_STUDY.name) {
          const { primary, duplicates } = selectSingleNamedStudy(
            chart.getAllStudies(),
            ORDERBOOK_HEATMAP_STUDY.name,
            heatmapStudyIdRef.current ?? undefined
          )
          if (primary) {
            heatmapStudyIdRef.current = primary.id
            for (const duplicate of duplicates) {
              try {
                chart.removeEntity(duplicate.id)
              } catch {}
            }
            syncOrderbookHeatmapFromStudy(primary.id)
          }
        }
      } else if (eventType === 'remove') {
        const removedKind = (
          Object.entries(visibleRangeProfileStudyIdsRef.current) as Array<
            [VisibleRangeProfileKind, TVEntityId]
          >
        ).find(([, studyId]) => studyId === entityId)?.[0]
        if (removedKind) {
          const fallback = chart
            .getAllStudies()
            .find((study) => study.name === VISIBLE_RANGE_PROFILE_STUDIES[removedKind].name)
          if (fallback) {
            visibleRangeProfileStudyIdsRef.current[removedKind] = fallback.id
            syncVisibleRangeProfileFromStudy(removedKind, fallback.id)
          } else {
            delete visibleRangeProfileStudyIdsRef.current[removedKind]
            syncVisibleRangeProfileFromStudy(removedKind, null)
          }
        }
        const removedLevelKind = (
          Object.entries(levelProfileStudyIdsRef.current) as Array<[LevelProfileKind, TVEntityId]>
        ).find(([, studyId]) => studyId === entityId)?.[0]
        if (removedLevelKind) {
          const fallback = chart
            .getAllStudies()
            .find((study) => study.name === LEVEL_PROFILE_STUDIES[removedLevelKind].name)
          if (fallback) {
            levelProfileStudyIdsRef.current[removedLevelKind] = fallback.id
            syncLevelProfileFromStudy(removedLevelKind, fallback.id)
          } else {
            delete levelProfileStudyIdsRef.current[removedLevelKind]
            syncLevelProfileFromStudy(removedLevelKind, null)
          }
        }
        if (heatmapStudyIdRef.current === entityId) {
          const fallback = chart
            .getAllStudies()
            .find((study) => study.name === ORDERBOOK_HEATMAP_STUDY.name)
          heatmapStudyIdRef.current = fallback?.id ?? null
          syncOrderbookHeatmapFromStudy(fallback?.id ?? null)
        }
      }

      syncActiveAnalyticsFromChart()
      syncSeriesSubscriptions()
      syncCompareSymbolLabels(widget)
      scheduleMeasureLegendPatch()
      if (eventType === 'create' || eventType === 'paste_study') {
        applyCompareStudyPalette(chart)
      }

      if (eventType === 'create') {
        // Only user-added studies: our own re-syncs are gated, and events in the
        // first moments after subscribing are the saved chart being restored.
        if (
          Date.now() - studySubscribedAt > STUDY_RESTORE_SETTLE_MS &&
          !isProgrammaticStudyCreatePending()
        ) {
          const createdForAnalytics = chart.getAllStudies().find((study) => study.id === entityId)
          if (createdForAnalytics?.name) {
            const [exchangePart, tickerPart] = chart.symbol().split(':')
            void track('Added Indicator', {
              indicator_name: createdForAnalytics.name,
              ...(tickerPart
                ? { exchange: exchangePart.toLowerCase(), ticker: tickerPart }
                : { ticker: exchangePart }),
            })
          }
        }
        // A freshly created study isn't always attached to its pane yet when
        // 'create' fires, so the merge-up in syncActiveAnalyticsFromChart can
        // land on a stale paneIndex() and no-op. Re-run once the pane settles.
        newStudyMergeTimeoutIds.push(setTimeout(syncActiveAnalyticsFromChart, 300))
      }

      clearTimeout(timeoutId)
      timeoutId = setTimeout(handleAutoSave, 1500)
    }

    // Input/property edits (e.g. CVD measure, ob-metrics depth tier) fire
    // study_properties_changed, never study_event. Programmatic refreshNonce
    // bumps fire it too, but syncSeriesSubscriptions is idempotent (the nonce is
    // excluded from the series key, so a bump re-acquires nothing) and the
    // autosave content diff strips the nonce — so a bump is a no-op both ways.
    // A real edit re-acquires under a new series key and persists.
    const studyPropertiesHandler = (entityId: string) => {
      const study = chart.getAllStudies().find((item) => item.id === entityId)
      const kind = study ? getVisibleRangeProfileKindByStudyName(study.name) : null
      if (kind) {
        visibleRangeProfileStudyIdsRef.current[kind] = entityId
        syncVisibleRangeProfileFromStudy(kind, entityId)
      }
      const levelKind = study ? getLevelProfileKindByStudyName(study.name) : null
      if (levelKind) {
        levelProfileStudyIdsRef.current[levelKind] = entityId
        syncLevelProfileFromStudy(levelKind, entityId)
      }
      if (study?.name === ORDERBOOK_HEATMAP_STUDY.name) {
        syncOrderbookHeatmapRangeFromStudy(entityId)
      }
      syncSeriesSubscriptions()
      scheduleMeasureLegendPatch()
      setIndicatorInstances(getActiveIndicatorInstancesFromWidget(widget))
      clearTimeout(timeoutId)
      timeoutId = setTimeout(handleAutoSave, 1500)
    }

    widget.subscribe('drawing_event', drawingHandler)
    widget.subscribe('study_event', studyHandler)
    widget.subscribe('study_properties_changed', studyPropertiesHandler)

    // Panning never fires study_event/study_properties_changed — without this,
    // an already-active own-fetch series (PCR/skew/options-volume/ob-metrics)
    // keeps whatever window it had at acquire time forever, no matter how far
    // back the user scrolls the main chart.
    let visibleRangeTimeoutId: ReturnType<typeof setTimeout>
    const visibleRangeSubscription = widget.activeChart().onVisibleRangeChanged()
    const visibleRangeHandler = (range: { from: number; to: number }) => {
      clearTimeout(visibleRangeTimeoutId)
      visibleRangeTimeoutId = setTimeout(() => {
        syncSeriesSubscriptions()

        const datafeed = datafeedRef.current
        const activeWidget = widgetRef.current
        if (
          !datafeed?.setCumulativeVolumeDeltaBaseline(range.from, {
            symbol,
            timeframeSeconds: timeframeRef.current,
          }) ||
          !activeWidget
        ) {
          return
        }

        const chart = activeWidget.activeChart()
        refreshNonceRef.current = nextRefreshNonce(refreshNonceRef.current)
        for (const studyId of getAnalyticsStudyMapFromWidget(activeWidget).get(
          'cumulativeVolumeDelta'
        ) ?? []) {
          try {
            chart
              .getStudyById(studyId)
              .setInputValues([{ id: REFRESH_NONCE_INPUT_ID, value: refreshNonceRef.current }])
          } catch {}
        }
      }, VISIBLE_RANGE_SYNC_DEBOUNCE_MS)

      // A pan away from "now" means the window no longer is the preset's.
      if (!activeRangeSelectionRef.current || Date.now() < rangeChangeGuardUntilRef.current) return
      const nowSec = Date.now() / 1000
      const realtimeToleranceSec = Math.max(timeframeRef.current * 2, 60)
      if (Math.abs(range.to - nowSec) > realtimeToleranceSec) {
        activeRangeSelectionRef.current = null
        setActiveRangeSelection(null)
      }
    }
    visibleRangeSubscription.subscribe(null, visibleRangeHandler)
    try {
      visibleRangeHandler(widget.activeChart().getVisibleRange())
    } catch {}

    return () => {
      clearTimeout(timeoutId)
      clearTimeout(visibleRangeTimeoutId)
      clearTimeout(restoreSyncTimeoutId)
      newStudyMergeTimeoutIds.forEach(clearTimeout)
      legendPatchTimeoutIds.forEach(clearTimeout)
      try {
        widget.unsubscribe('drawing_event', drawingHandler)
        widget.unsubscribe('study_event', studyHandler)
        widget.unsubscribe('study_properties_changed', studyPropertiesHandler)
        visibleRangeSubscription.unsubscribe(null, visibleRangeHandler)
      } catch {}
    }
  }, [
    isChartReady,
    cardId,
    setChartOption,
    syncConfig,
    colorThemeRef,
    syncActiveAnalyticsFromChart,
    syncSeriesSubscriptions,
    syncVisibleRangeProfileFromStudy,
    syncLevelProfileFromStudy,
    syncOrderbookHeatmapFromStudy,
    syncOrderbookHeatmapRangeFromStudy,
    patchMeasureLegendLabels,
    symbol,
    widgetRef,
  ])

  // 6. Effects
  useEffect(() => {
    tradesRef.current = trades
  }, [trades])

  useEffect(() => {
    positionsRef.current = positions
  }, [positions])

  useEffect(() => {
    openOrdersRef.current = openOrders
  }, [openOrders])

  useEffect(() => {
    scaleLevelsRef.current = scaleLevels
  }, [scaleLevels])

  useEffect(() => {
    if (!realtimeEnabled) return
    datafeedRef.current?.pushQuote(
      { exchange: exchangeEnum, symbol },
      {
        lastPrice: symbolLastPrice ?? symbolMarkPrice,
        bidPrice: symbolBestBidPrice,
        askPrice: symbolBestAskPrice,
      }
    )
  }, [
    exchangeEnum,
    symbol,
    symbolBestAskPrice,
    symbolBestBidPrice,
    symbolLastPrice,
    symbolMarkPrice,
    realtimeEnabled,
  ])

  // Applies an explicit "last N seconds up to now" window — used by range-preset clicks
  // (5y/1y/6m/.../1d in chart-toolbar.tsx) instead of the generic chartReset/timeScaleReset
  // snap-to-default used everywhere else.
  //
  // Re-asserted once after the first attempt settles: when the preset also changed the
  // resolution, this runs right after resetData(), while the new resolution's bars are still
  // being fetched. TradingView clamps setVisibleRange to the range it currently has data for,
  // so that first call lands on a much shorter window than requested.
  const applyVisibleWindow = useCallback(
    // getWindow runs per attempt, so an "up to now" window re-anchors to now each time.
    // atLatest: frame the latest candle with TV's initial right margin (presets only).
    (getWindow: () => { from: number; to: number }, atLatest: boolean) => {
      if (!widgetRef.current) return
      const requestId = ++visibleRangeRequestIdRef.current
      clearTimeout(visibleRangeRetryTimeoutRef.current)

      const apply = (attempt: number) => {
        // A newer preset click (or a teardown) invalidates any pending re-assert.
        if (visibleRangeRequestIdRef.current !== requestId || !widgetRef.current) return
        rangeChangeGuardUntilRef.current = Date.now() + VISIBLE_RANGE_GUARD_MS
        let result: unknown
        try {
          result = widgetRef.current.activeChart().setVisibleRange(
            getWindow(),
            // Reapply TV's initial right margin so every range-preset click frames the
            // latest candle the same way as a freshly rendered chart.
            { applyDefaultRightMargin: atLatest }
          )
        } catch {}

        if (attempt >= VISIBLE_RANGE_APPLY_ATTEMPTS) return
        const promise = result as Promise<void> | undefined
        if (promise && typeof promise.then === 'function') {
          // Resolves once TradingView has the data for the requested window, so the
          // re-assert is a no-op when the first attempt already landed correctly.
          promise.then(() => apply(attempt + 1)).catch(() => {})
        } else {
          visibleRangeRetryTimeoutRef.current = setTimeout(
            () => apply(attempt + 1),
            VISIBLE_RANGE_REAPPLY_DELAY_MS
          )
        }
      }

      apply(1)
    },
    [widgetRef]
  )

  // "Last N seconds up to now" — re-anchored to now on every attempt so a slow bar fetch
  // doesn't leave the window trailing behind realtime.
  const applyVisibleRangeSeconds = useCallback(
    (rangeSeconds: number) =>
      applyVisibleWindow(() => {
        const toSec = Math.floor(Date.now() / 1000)
        return { from: toSec - rangeSeconds, to: toSec }
      }, true),
    [applyVisibleWindow]
  )

  useEffect(
    () => () => {
      visibleRangeRequestIdRef.current++
      clearTimeout(visibleRangeRetryTimeoutRef.current)
    },
    []
  )

  useEffect(() => {
    // Timeframe change: reset bar tracker, snap back to realtime, then reload overlays
    if (!isChartReady || !widgetRef.current) return
    const requestId = ++timeframeChangeRequestIdRef.current
    lastPushedBarTimeRef.current = 0
    const pendingRangeSeconds = pendingVisibleRangeSecondsRef.current
    pendingVisibleRangeSecondsRef.current = null
    if (isNativeResolutionChangeRef.current) {
      // TV already switched resolution and framed the range itself — just follow it.
      isNativeResolutionChangeRef.current = false
      if (!symbolChangeInFlightRef.current) loadOrderOverlays(symbol, timeframe)
      syncActiveAnalyticsFromChart()
      syncSeriesSubscriptions()
      return
    }
    isSelfInitiatedResolutionChangeRef.current = true
    widgetRef.current.chart().setResolution(chartResolution(timeframe, tick), () => {
      const widget = widgetRef.current
      if (!widget || requestId !== timeframeChangeRequestIdRef.current) return

      const chart = widget.activeChart()
      // setResolution's callback runs before the replacement history has necessarily
      // arrived. Resetting the time scale there uses the previous resolution's loaded
      // range and can leave a large future offset after the new bars are rendered.
      // Listen before resetData so even an immediate cached response is observed, then
      // align once after TradingView has committed the new history. A range preset frames
      // its own window instead (applyVisibleRangeSeconds below).
      if (pendingRangeSeconds === null) {
        chart.onDataLoaded().subscribe(
          null,
          () => {
            requestAnimationFrame(() => {
              if (requestId !== timeframeChangeRequestIdRef.current || !widgetRef.current) return
              try {
                widgetRef.current.executeActionById('chartReset')
              } catch {}
              try {
                widgetRef.current.executeActionById('timeScaleReset')
              } catch {}
            })
          },
          true
        )
      }

      try {
        chart.resetData()
      } catch {}
      if (pendingRangeSeconds !== null) applyVisibleRangeSeconds(pendingRangeSeconds)
      if (!symbolChangeInFlightRef.current) loadOrderOverlays(symbol, timeframe)
      syncActiveAnalyticsFromChart()
      syncSeriesSubscriptions()
      // Deferred a microtask past the callback's own synchronous body — TradingView's
      // onIntervalChanged echo for this exact setResolution() call can land essentially
      // back-to-back with this callback rather than strictly before it, so clearing
      // synchronously here risked reopening the window right as the echo arrives.
      void Promise.resolve().then(() => {
        if (requestId === timeframeChangeRequestIdRef.current) {
          isSelfInitiatedResolutionChangeRef.current = false
        }
      })
    })
    // symbol intentionally excluded — handled by symbol/exchange effect below
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    timeframe,
    tick,
    isChartReady,
    syncActiveAnalyticsFromChart,
    syncSeriesSubscriptions,
    applyVisibleRangeSeconds,
  ])

  // A range preset (e.g. "1y" = 1d candles over the last 365 days) bundles a candle interval
  // with a visible-range window. If the interval is unchanged (e.g. "1y" → "6m", both 1d), the
  // timeframe prop won't change and the effect above won't re-fire — apply the range here.
  // Otherwise stash it for that effect to pick up once the resolution change lands.
  const handleRangePresetChange = useCallback(
    (preset: ChartRangePreset) => {
      const selection = { label: preset.label, timeframe: preset.timeframeSeconds }
      activeRangeSelectionRef.current = selection
      setActiveRangeSelection(selection)
      if (preset.timeframeSeconds === timeframeRef.current && !tick) {
        applyVisibleRangeSeconds(preset.rangeSeconds)
      } else {
        pendingVisibleRangeSecondsRef.current = preset.rangeSeconds
        // The resolution change itself fires onVisibleRangeChanged before
        // applyVisibleRangeSeconds gets to arm the guard — without this, that event can
        // read as a user pan and drop the just-made selection.
        rangeChangeGuardUntilRef.current = Date.now() + VISIBLE_RANGE_GUARD_MS
        onTimeframeChange(preset.timeframeSeconds)
      }
    },
    [applyVisibleRangeSeconds, onTimeframeChange, tick]
  )

  const handleToolbarTimeframeChange = useCallback(
    (nextTimeframe: number) => {
      activeRangeSelectionRef.current = null
      setActiveRangeSelection(null)
      onTimeframeChange(nextTimeframe)
    },
    [onTimeframeChange]
  )

  // Go to (ChartToolbar's ChartGoToPanel). TradingView's own Go to dialog throws inside its
  // gotoTime for a date before the loaded history (`_density`: "Value is undefined"), so it never
  // moved — frame the window ourselves, re-asserted like a range preset while history loads.
  const handleGoToTime = useCallback(
    (at: Date) => {
      activeRangeSelectionRef.current = null
      setActiveRangeSelection(null)
      const halfSpan = Math.max(timeframeRef.current, 1) * (GO_TO_DATE_BARS / 2)
      const center = Math.floor(at.getTime() / 1000)
      applyVisibleWindow(
        () => ({
          from: center - halfSpan,
          to: Math.min(center + halfSpan, Math.floor(Date.now() / 1000)),
        }),
        false
      )
    },
    [applyVisibleWindow]
  )
  const handleGoToRange = useCallback(
    (from: Date, to: Date) => {
      activeRangeSelectionRef.current = null
      setActiveRangeSelection(null)
      applyVisibleWindow(
        () => ({
          from: Math.floor(from.getTime() / 1000),
          to: Math.min(Math.floor(to.getTime() / 1000), Math.floor(Date.now() / 1000)),
        }),
        false
      )
    },
    [applyVisibleWindow]
  )

  // The side panel is non-modal and the chart is an iframe, so Radix never sees clicks on it:
  // close the picker on a press anywhere in the chart ourselves. Skip the Indicators button
  // (it toggles on click) and TV's overlap-manager root, which hosts TV's own dialogs and
  // menus, e.g. the full settings dialog opened from the panel.
  useEffect(() => {
    if (!isExpandedIndicatorPickerOpen || !isChartReady) return
    const iframeDoc = containerRef.current?.querySelector('iframe')?.contentDocument
    if (!iframeDoc) return
    const handlePointerDown = (event: PointerEvent) => {
      // Cross-realm target: `instanceof Element` is false for iframe nodes, so duck-type it.
      const target = event.target as Element | null
      if (target?.closest?.('.orderx-tv-header-button-slot--indicators, #overlap-manager-root')) {
        return
      }
      setIsExpandedIndicatorPickerOpen(false)
    }
    iframeDoc.addEventListener('pointerdown', handlePointerDown, true)
    return () => iframeDoc.removeEventListener('pointerdown', handlePointerDown, true)
  }, [containerRef, isChartReady, isExpandedIndicatorPickerOpen])

  useEffect(() => {
    if (!isChartReady || !widgetRef.current) return

    const intervalChanged = widgetRef.current.activeChart().onIntervalChanged()
    const handleIntervalChanged = (resolution: string) => {
      // Ignore the echo of a resolution change WE just made (range-preset or toolbar
      // click, both go through the timeframe-change effect's own chart.setResolution()
      // call) — see isSelfInitiatedResolutionChangeRef's own comment for why comparing
      // against timeframeRef.current alone isn't a reliable enough guard on its own.
      if (isSelfInitiatedResolutionChangeRef.current) return
      const nextTimeframe =
        resolution === CHART_TICK_RESOLUTION
          ? CHART_TICK_TIMEFRAME
          : resolutionToSeconds(resolution)
      if (
        !Number.isFinite(nextTimeframe) ||
        (nextTimeframe <= 0 && nextTimeframe !== CHART_TICK_TIMEFRAME) ||
        nextTimeframe === pickerTimeframeRef.current
      ) {
        return
      }

      pickerTimeframeRef.current = nextTimeframe
      timeframeRef.current = chartDataTimeframe(nextTimeframe)
      isNativeResolutionChangeRef.current = true
      activeRangeSelectionRef.current = null
      setActiveRangeSelection(null)
      onTimeframeChange(nextTimeframe)
      syncConfig(cardId, {
        timeframe: nextTimeframe.toString(),
        chartType: chartTypeRef.current.toString(),
      })
    }

    intervalChanged.subscribe(null, handleIntervalChanged)
    return () => intervalChanged.unsubscribe(null, handleIntervalChanged)
  }, [cardId, isChartReady, onTimeframeChange, syncConfig, widgetRef])

  useEffect(() => {
    // Price scale mode (Normal / Log / %) and TradingView-style auto-scale.
    if (!isChartReady || !widgetRef.current) return
    const mode = PRICE_SCALE_MODE_MAP[chartOption?.scale ?? 'Normal'] ?? 0
    const autoScale = chartOption?.autoScale ?? true
    try {
      const priceScale = getMainPriceScale()
      priceScale?.setMode(mode)
      priceScale?.setAutoScale(autoScale)
      if (autoScale) {
        widgetRef.current.executeActionById('Chart.PriceScale.Reset')
      }
    } catch {}
  }, [chartOption?.autoScale, chartOption?.scale, getMainPriceScale, isChartReady, widgetRef])

  useEffect(() => {
    // Manual y-axis drag/zoom disables auto-scale inside TradingView. Keep our
    // toolbar state in sync with the native price scale state.
    if (!isChartReady || !widgetRef.current) return

    // Same for the native bottom toolbar's % / Log buttons, which change the mode directly.
    const timer = setInterval(() => {
      try {
        const priceScale = getMainPriceScale()
        const nextAutoScale = priceScale?.isAutoScale()
        if (
          typeof nextAutoScale === 'boolean' &&
          nextAutoScale !== (chartOption?.autoScale ?? true)
        ) {
          setChartOption((prev) => ({ ...prev, autoScale: nextAutoScale }))
          syncConfig(cardId, { autoScale: String(nextAutoScale) })
        }
        const nextScale = PRICE_SCALE_MODE_NAMES[priceScale?.getMode() ?? -1]
        if (nextScale && nextScale !== (chartOption?.scale ?? 'Normal')) {
          setChartOption((prev) => ({ ...prev, scale: nextScale }))
          syncConfig(cardId, { scale: nextScale })
        }
      } catch {}
    }, 500)

    return () => clearInterval(timer)
  }, [
    cardId,
    chartOption?.autoScale,
    chartOption?.scale,
    getMainPriceScale,
    isChartReady,
    setChartOption,
    syncConfig,
    widgetRef,
  ])

  useEffect(() => {
    // Chart type (Candles / Bars / Line / Area / etc.)
    if (!isChartReady || !widgetRef.current) return
    try {
      // Tick always draws as a continuous line; leaving it restores the chosen type.
      widgetRef.current
        .chart()
        .setChartType(tick ? TV_AREA_CHART_TYPE : (chartOption?.chartType ?? 1))
    } catch {}
  }, [chartOption?.chartType, isChartReady, tick, widgetRef])

  useEffect(() => {
    // Symbol / exchange change: reset bar tracker, snap back to realtime, then reload overlays.
    // setSymbol is an async postMessage round-trip into the TV iframe — if `symbol`
    // changes again before it resolves, the stale callback must not clobber the
    // newer request's state. A per-effect-run generation id lets the callback tell
    // whether it's still current.
    if (!isChartReady || !widgetRef.current) return
    lastPushedBarTimeRef.current = 0
    const requestId = ++symbolChangeRequestIdRef.current
    symbolChangeInFlightRef.current = true
    clearIndexPriceLine()
    clearTradingOverlays()

    const finishSymbolChange = () => {
      if (symbolChangeRequestIdRef.current !== requestId) return
      symbolChangeInFlightRef.current = false
      loadOrderOverlays(symbol, timeframe)
      recreateIndexPriceLineRef.current()
      syncActiveAnalyticsFromChartRef.current()
      syncSeriesSubscriptionsRef.current()
    }

    widgetRef.current.setSymbol(symbol, chartResolution(timeframe, tick), () => {
      if (symbolChangeRequestIdRef.current !== requestId) return
      const widget = widgetRef.current
      if (!widget) return
      try {
        widget.activeChart().resetData()
      } catch {}
      try {
        widget.executeActionById('chartReset')
      } catch {}
      try {
        widget.executeActionById('timeScaleReset')
      } catch {}

      // TradingView can invoke this callback even when the iframe silently
      // dropped/ignored the request (e.g. it raced with the previous setSymbol
      // call). Re-verify the chart actually landed on the requested symbol; if
      // not, reissue once for the current props instead of leaving the iframe
      // stuck on the old one.
      let appliedSymbol: string | undefined
      try {
        appliedSymbol = widget.activeChart().symbol()
      } catch {}
      if (appliedSymbol !== symbol) {
        widget.setSymbol(symbol, chartResolution(timeframe, tick), finishSymbolChange)
        return
      }
      finishSymbolChange()
    })
    // timeframe intentionally excluded — handled by timeframe effect above.
    // syncActiveAnalyticsFromChart/syncSeriesSubscriptions intentionally excluded
    // too — both are read via refs above so this effect stays symbol/exchange-only;
    // they're recreated every timeframe change, and listing them here used to
    // re-run this whole effect (and its setSymbol + resetData) on every timeframe
    // switch, racing the timeframe-change effect.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol, exchange, isChartReady])

  // Structural signal for the current symbol only — deliberately excludes
  // markPrice/unrealizedPnl so a full overlay clear+recreate doesn't fire on every
  // price tick (that was the cause of the entry/exit indicators flickering). Cosmetic
  // PnL updates are handled separately below via updatePositionPnlDisplay.
  const overlaySignal = useMemo(() => {
    const orderKey = openOrders
      .filter((item) => item.order?.symbol === symbol)
      .map(
        (item) =>
          `${item.order?.accountId}:${item.order?.orderId}:${item.order?.tradeVenue}:${item.order?.price}:${item.order?.quantity}:${item.order?.side}:${item.order?.status}:${item.order?.label}`
      )
      .sort()
      .join(',')
    const tradeKey = trades
      .filter((item) => item.order?.symbol === symbol)
      .map(
        (item) =>
          `${item.order?.accountId}:${item.order?.orderId}:${item.order?.tradeVenue}:${item.order?.status}:${item.order?.avgFillPrice}:${item.order?.price}:${item.order?.filledQuantity}:${item.order?.quantity}:${item.order?.side}:${item.order?.updatedAt}:${item.order?.createdAt}:${item.order?.label}`
      )
      .sort()
      .join(',')
    const positionKey = positions
      .filter((item) => item.symbol === symbol)
      .map(
        (position) =>
          `${position.accountId}:${position.exchange}:${position.tradeVenue}:${position.quantity}:${position.avgPrice}:${position.side}:${position.sizeDenomination}:${position.liquidationPrice}:${position.legs?.map((leg) => `${leg.quantity}@${leg.liquidationPrice}`).join('/')}`
      )
      .sort()
      .join(',')
    const scaleKey = scaleLevels
      .map((level) => `${level.key}:${level.price}:${level.quantity}`)
      .join(',')
    const accountKey = userAccounts
      .filter(
        (account) =>
          account.status === AccountStatus.ACCOUNT_STATUS_ACTIVE &&
          account.tradeVenue === resolvedTradeVenue
      )
      .map((account) => `${account.accountId}:${account.name}`)
      .sort()
      .join(',')
    return `${chartAccountId ?? ''}|${resolvedTradeVenue ?? TradeVenue.TRADE_VENUE_UNSPECIFIED}|${accountKey}|${orderKey}|${scaleKey}|${tradeKey}|${positionKey}`
  }, [
    trades,
    positions,
    openOrders,
    scaleLevels,
    symbol,
    userAccounts,
    chartAccountId,
    resolvedTradeVenue,
  ])

  useEffect(() => {
    // Reload execution markers, position line, buttons, and open order lines on trading state
    // changes. The 200ms debounce coalesces bursts (a fill arrives together with position and
    // order updates) — it is intentionally NOT applied to user setting toggles below.
    if (!isChartReady) return
    const timer = setTimeout(() => {
      if (!symbolChangeInFlightRef.current) loadOrderOverlays(symbol, timeframe)
    }, 200)
    return () => clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [overlaySignal, isChartReady])

  // Chart-settings toggles are single user actions: reflect them immediately
  // (no debounce). Trading history is already supplied by allTradesAtom, so
  // toggles only rebuild local overlay shapes and never trigger a REST request.
  const chartSettingsKey = [
    chartOption?.positionSizeUnit,
    chartOption?.showPositionLine,
    chartOption?.showLiquidationLine,
    chartOption?.showBuySellTriangles,
    chartOption?.showBuySellButtons,
    chartOption?.showOpenOrders,
  ].join('|')
  const prevChartSettingsKeyRef = useRef<string | null>(null)
  useEffect(() => {
    if (!isChartReady) return
    // First run after chart-ready only records the baseline — the initial overlay
    // load already happens in the chart-ready / symbol-change paths.
    if (prevChartSettingsKeyRef.current === null) {
      prevChartSettingsKeyRef.current = chartSettingsKey
      return
    }
    if (prevChartSettingsKeyRef.current === chartSettingsKey) return
    prevChartSettingsKeyRef.current = chartSettingsKey
    if (!symbolChangeInFlightRef.current) loadOrderOverlays(symbol, timeframe)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [chartSettingsKey, isChartReady])

  useEffect(() => {
    // Cheap in-place PnL label refresh — runs on every position tick without
    // clearing/recreating any overlay shapes.
    if (!isChartReady) return
    updatePositionPnlDisplay(symbol)
  }, [positions, symbol, chartLiveMarkPrice, isChartReady, updatePositionPnlDisplay])

  useEffect(() => {
    return () => clearTradingOverlays()
  }, [clearTradingOverlays])

  useEffect(() => {
    // Unmount only: abort in-flight series fetches, clear grace timers, drop caches.
    return () => {
      datafeed.seriesManager.destroy()
      datafeed.comparisonSeriesManager.destroy()
    }
  }, [datafeed])

  useEffect(() => {
    if (!isChartReady || !onRegisterDrawingToggle) return
    onRegisterDrawingToggle(() => {
      const chart = widgetRef.current?.activeChart()
      if (!chart) return false
      chart.executeActionById('drawingToolbarAction')
      return chart.getCheckableActionState('drawingToolbarAction')
    })
  }, [isChartReady, onRegisterDrawingToggle, widgetRef])

  useEffect(() => {
    if (!isChartReady || !widgetRef.current || !onDrawingPanelVisibilityChange) return
    const widget = widgetRef.current
    const handler = (isHidden: unknown) => onDrawingPanelVisibilityChange(!(isHidden as boolean))
    widget.subscribe('toggle_sidebar', handler as (params: unknown) => void)
    return () => {
      try {
        widget.unsubscribe('toggle_sidebar', handler as (params: unknown) => void)
      } catch {}
    }
  }, [isChartReady, onDrawingPanelVisibilityChange, widgetRef])

  // Apply persisted drawing panel visibility on chart ready.
  // TradingView always starts with the panel visible, so hide it if saved state is false.
  useEffect(() => {
    if (!isChartReady || initialDrawingPanelVisible !== false) return
    const chart = widgetRef.current?.activeChart()
    if (!chart) return
    const isVisible = chart.getCheckableActionState('drawingToolbarAction')
    if (isVisible) {
      chart.executeActionById('drawingToolbarAction')
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isChartReady])

  useEffect(() => {
    recreateIndexPriceLine()
    return () => {
      clearIndexPriceLine()
    }
  }, [clearIndexPriceLine, recreateIndexPriceLine])

  useEffect(() => {
    try {
      indexPriceLineRef.current?.setPrice(indexLinePrice)
    } catch {}
  }, [indexLinePrice])

  // Volume/Liquidation/OI (from visibleRangeProfileQuery, HTTP visible-range request) and
  // Liquidation/Stop Levels (from useLevelProfile, AnalyticsStream) are two structurally
  // different data pipelines that share one screen-anchored canvas — see tv-profile-canvas.ts. Each
  // family computes its own buckets/projection independently; the two level-profile lanes
  // additionally never share a grid with each other (see the doc's "different price_min/
  // bucket_width" note), which projectBucket already handles per-lane since it's built fresh
  // per lane call.
  const profileLanes = useMemo<LaneDescriptor[]>(() => {
    const lanes: LaneDescriptor[] = []

    if (visibleRangeProfileEnabled) {
      const status: LaneDescriptor['status'] =
        visibleRangeProfileQuery.isPending || visibleRangeProfileQuery.isFetching
          ? 'loading'
          : visibleRangeProfileQuery.isError
            ? 'error'
            : 'ready'
      const profile = visibleRangeProfileQuery.data
      const unitByKind: Record<VisibleRangeProfileKind, VisibleRangeProfileUnit> = {
        volume: volumeDeltaProfileUnit,
        liquidation: liquidationProfileUnit,
        openInterest: openInterestProfileUnit,
      }
      const labelsByKind: Record<VisibleRangeProfileKind, { negative: string; positive: string }> =
        {
          volume: { negative: 'Sell', positive: 'Buy' },
          liquidation: { negative: 'Sell', positive: 'Buy' },
          openInterest: { negative: 'Decrease', positive: 'Increase' },
        }
      const showLabelsByKind: Record<VisibleRangeProfileKind, boolean> = {
        volume: volumeDeltaProfileShowLabels,
        liquidation: liquidationProfileShowLabels,
        openInterest: openInterestProfileShowLabels,
      }
      activeVisibleRangeProfiles.forEach((kind) => {
        const buckets = profile
          ? selectProfileBuckets(
              profile,
              kind,
              unitByKind[kind],
              visibleRangeProfileSettingsByKind[kind],
              symbolTickSize ? Number(symbolTickSize) : undefined
            )
          : []
        const labels = labelsByKind[kind]
        const colors = PROFILE_LANE_COLORS[kind]
        lanes.push({
          key: kind,
          status,
          buckets,
          projectBucket: createFullBucketRangeProjector(buckets),
          positiveLabel: labels.positive,
          negativeLabel: labels.negative,
          positiveColor: colors.positive,
          negativeColor: colors.negative,
          volumeMode: visibleRangeProfileSettingsByKind[kind].volumeMode,
          showLabels: showLabelsByKind[kind],
          emptyLabel: 'No activity',
        })
      })
    }

    if (levelProfileEnabled) {
      const visibleMin = chartPriceScale.visiblePriceRange?.from ?? 0
      const visibleMax = chartPriceScale.visiblePriceRange?.to ?? 0
      const projectBucket = createPriceRangeProjector(visibleMin, visibleMax)

      if (liquidationLevelProfileEnabled) {
        const buckets =
          liquidationLevelProfile.status === 'ready'
            ? selectLiquidationLevelBuckets(
                liquidationLevelProfile.profile,
                liquidationLevelProfileMeasure
              )
            : []
        lanes.push({
          key: 'liquidationLevels',
          status: liquidationLevelProfile.status,
          buckets,
          projectBucket,
          positiveLabel: 'Long',
          negativeLabel: 'Short',
          positiveColor: PROFILE_LANE_COLORS.liquidationLevels.positive,
          negativeColor: PROFILE_LANE_COLORS.liquidationLevels.negative,
          showLabels: liquidationLevelProfileShowLabels,
          emptyLabel: 'No levels',
          notPublishedLabel: 'Not yet published',
        })
      }

      if (stopLevelProfileEnabled) {
        const buckets =
          stopLevelProfile.status === 'ready'
            ? selectStopLevelBuckets(stopLevelProfile.profile, stopLevelProfileMeasure)
            : []
        lanes.push({
          key: 'stopLevels',
          status: stopLevelProfile.status,
          buckets,
          projectBucket,
          positiveLabel: 'Take Profit',
          negativeLabel: 'Stop Loss',
          positiveColor: PROFILE_LANE_COLORS.stopLevels.positive,
          negativeColor: PROFILE_LANE_COLORS.stopLevels.negative,
          showLabels: stopLevelProfileShowLabels,
          emptyLabel: 'No levels',
          notPublishedLabel: 'Not yet published',
        })
      }
    }

    return lanes
  }, [
    activeVisibleRangeProfiles,
    chartPriceScale.visiblePriceRange,
    levelProfileEnabled,
    liquidationLevelProfile,
    liquidationLevelProfileEnabled,
    liquidationLevelProfileMeasure,
    liquidationLevelProfileShowLabels,
    liquidationProfileShowLabels,
    liquidationProfileUnit,
    openInterestProfileShowLabels,
    openInterestProfileUnit,
    stopLevelProfile,
    stopLevelProfileEnabled,
    stopLevelProfileMeasure,
    stopLevelProfileShowLabels,
    symbolTickSize,
    visibleRangeProfileEnabled,
    visibleRangeProfileQuery.data,
    visibleRangeProfileQuery.isError,
    visibleRangeProfileQuery.isFetching,
    visibleRangeProfileQuery.isPending,
    visibleRangeProfileSettingsByKind,
    volumeDeltaProfileShowLabels,
    volumeDeltaProfileUnit,
  ])

  useEffect(() => {
    const widget = widgetRef.current
    const container = containerRef.current
    if (!isChartReady || !widget || !container || profileLanes.length === 0) return
    return mountTVProfileCanvas(widget.activeChart(), container, profileLanes)
  }, [containerRef, isChartReady, profileLanes, widgetRef])

  // 7. Render
  return (
    <div className="relative flex h-full w-full flex-col">
      <div className="relative flex min-h-0 w-full flex-1">
        <div
          ref={containerRef}
          // Counted by the runtime recovery signal collector. Marked on the
          // container we own rather than matched against TradingView's internal
          // iframe id, which is theirs to change.
          data-runtime-widget="tradingview"
          className="relative z-30 h-full min-h-0 min-w-0 flex-1 overflow-hidden [&_.chart-container]:bg-transparent! [&_iframe]:absolute [&_iframe]:inset-0 [&_iframe]:block [&_iframe]:h-full! [&_iframe]:w-full! [&_iframe]:bg-transparent!"
        />
        {/* The iframe boots blank (and half-drawn) for a while: cover it until TV is ready. */}
        {!isChartReady && (
          <ChartSkeleton className="bg-page pointer-events-none absolute inset-0 z-40" />
        )}
        {orderbookHeatmapVisible && isChartReady ? (
          <OrderbookHeatmapOverlay
            widget={widgetRef.current}
            containerRef={containerRef}
            heatmap={orderbookHeatmap.heatmap.data}
            sizeFilter={orderbookHeatmapSizeFilter}
            grouping={orderbookHeatmapGrouping}
            rangePct={orderbookHeatmapRange}
            live={orderbookHeatmap.atLiveEdge}
            exchange={exchangeEnum}
            symbol={symbol}
            timeframe={timeframe}
          />
        ) : null}
      </div>
      <div className="rgl-chart-overlay absolute inset-0 z-50 cursor-move bg-transparent" />

      <ChartToolbar
        cardId={cardId}
        exchange={exchangeEnum}
        timeframe={timeframe}
        activeRangeLabel={
          activeRangeSelection?.timeframe === timeframe ? activeRangeSelection.label : null
        }
        onTimeframeChange={handleToolbarTimeframeChange}
        onRangePresetChange={handleRangePresetChange}
        onGoToTime={handleGoToTime}
        onGoToRange={handleGoToRange}
      />

      {isExpandedIndicatorPickerOpen ? (
        <ChartIndicatorPickerDialog
          onOpenChange={setIsExpandedIndicatorPickerOpen}
          activeIndicatorIds={activeIndicatorIds}
          activeInstances={indicatorInstances}
          onToggleIndicator={onToggleIndicator}
          onRemoveInstance={onRemoveIndicatorInstance}
          onToggleInstanceVisibility={onToggleIndicatorInstanceVisibility}
          onOpenInstanceSettings={onOpenIndicatorInstanceSettings}
          settingsApi={indicatorSettingsApi}
          showVisibleRangeProfiles={!isOption}
          showLiquidationProfile={LIQUIDATION_PROFILE_EXCHANGES.has(exchangeEnum)}
          showOpenInterestProfile={isOpenInterestProfileSupported(symbolInstrument)}
          showLiquidationLevelProfile={isHyperliquidPerp && isNormalNonInvertedScale}
          showStopLevelProfile={isHyperliquidPerp && isNormalNonInvertedScale}
          showOrderbookHeatmap={isOrderbookHeatmapSupported}
        />
      ) : null}

      {isComparePickerOpen ? (
        <WatchlistWizard
          open
          onOpenChange={setIsComparePickerOpen}
          mode="single"
          onSelect={handleCompareSelect}
          chartCompare={compareMenuOptions}
        />
      ) : null}
    </div>
  )
}
